import { CurrencyAccountsPanel } from "@/features/group_finance/components/CurrencyAccountsPanel";
import { useState, useMemo, useEffect, useCallback, useRef } from "react";
import { Spinner } from "@/components/ui/Spinner";
import { Modal } from "@/components/ui/Modal";
import { DatePicker } from "@/components/ui/DatePicker";
import { useSearchParams } from "react-router-dom";
import { useAuthStore } from "@/store/auth";
import { Plus, Trash2 } from "lucide-react";
import { formatAmount } from "@/utils/formatAmount";
import { formatDate } from "@/utils/formatDate";
import {
  useLatestBalances,
  useCreateBalance,
  useCompanyBalances,
  useDeleteBalance,
  usePlannedEntries,
  useCreatePlannedEntry,
  useDeletePlannedEntry,
  useNonLiquidAssets,
  useCreateAsset,
  useDeleteAsset,
  useVATEntries,
  useCreateVATEntry,
  useDeleteVATEntry,
  useVATOverpaid,
  useVATCollected,
  useGroupCashflow,
  useVATSuggestion,
  useCITSuggestion,
  useMarkVATEntryPaid,
  useBalanceHistory,
  useCompanies,
  useIntercompanyRecurring,
  useCreateIntercompanyRecurring,
  useUpdateIntercompanyRecurring,
  useDeleteIntercompanyRecurring,
  useIntercompanyOneTime,
  useCreateIntercompanyOneTime,
  useDeleteIntercompanyOneTime,
  useMonthlyExtraCosts,
  useSetMonthlyExtraCost,
  useMonthlyPrzRevenue,
  useOutstandingReceivables,
  useCurrencyAccounts,
} from "@/features/group_finance/hooks/useGroupFinance";
import type {
  GroupPlannedEntry,
  IntercompanyRecurring,
  IntercompanyOneTime,
} from "@/features/group_finance/api/groupFinance";
import type { CashflowEvent } from "@/features/enterprises/types";
import { CashflowChart } from "@/features/finances/components/CashflowChart";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

// ── Helpers ────────────────────────────────────────────────────────────────

function fmt(val: string | null | undefined) {
  if (!val) return "—";
  return formatAmount(val) + " PLN";
}

function fmtDate(d: string | null | undefined) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("pl-PL");
}

/** "2026-10" / "2026-10-01" → "10.2026" */
function fmtPeriod(p: string): string {
  return `${p.slice(5, 7)}.${p.slice(0, 4)}`;
}

// Balances are entered by hand, so a two-month-old figure looks exactly like
// yesterday's unless the age is spelled out. Thresholds are a judgement call:
// a week is normal slippage, a month means the number can't carry a decision.
const BALANCE_STALE_DAYS = 8;
const BALANCE_VERY_STALE_DAYS = 31;

export function balanceAge(d: string | null | undefined): {
  days: number;
  label: string;
  stale: boolean;
  cls: string;
} | null {
  if (!d) return null;
  const then = new Date(d);
  if (Number.isNaN(then.getTime())) return null;
  const today = new Date();
  then.setHours(0, 0, 0, 0);
  today.setHours(0, 0, 0, 0);
  const days = Math.round((today.getTime() - then.getTime()) / 86_400_000);
  if (days < 0) return null; // future-dated entry — nothing useful to say
  const label = days === 0 ? "dzisiaj" : days === 1 ? "wczoraj" : `${days} dni temu`;
  const cls =
    days >= BALANCE_VERY_STALE_DAYS
      ? "text-red-600 dark:text-red-400 font-semibold"
      : days >= BALANCE_STALE_DAYS
        ? "text-amber-600 dark:text-amber-400 font-medium"
        : "text-gray-400";
  return { days, label, stale: days >= BALANCE_STALE_DAYS, cls };
}

type Tab = "salda" | "plan" | "vat" | "refaktury" | "wykres";

// ── Tab: Salda kont ────────────────────────────────────────────────────────

function BalancesTab() {
  const [, setSearchParams] = useSearchParams();
  const { user } = useAuthStore();
  const isCeo = user?.is_ceo ?? false;
  const { data: latest = [], isLoading } = useLatestBalances();
  const { mutate: createBalance, isPending: creating } = useCreateBalance();
  const { mutate: deleteBalance } = useDeleteBalance();
  const { data: balanceHistory = [] } = useBalanceHistory();
  const { data: companies = [] } = useCompanies();
  const [form, setForm] = useState<{
    company: string;
    date: string;
    net_balance: string;
    vat_balance: string;
    notes: string;
  }>({
    company: "",
    date: new Date().toISOString().slice(0, 10),
    net_balance: "0",
    vat_balance: "0",
    notes: "",
  });
  const [addingFor, setAddingFor] = useState<string | null>(null);
  const { data: history = [] } = useCompanyBalances(addingFor ?? undefined);

  // Foreign currency accounts
  const { data: currencyAccounts = [] } = useCurrencyAccounts();
  const totalFxPln = currencyAccounts.reduce(
    (s, a) => s + parseFloat(a.balance_pln ?? "0"),
    0,
  );

  // Non-liquid assets
  const { data: assets = [] } = useNonLiquidAssets();
  const { mutate: createAsset, isPending: creatingAsset } = useCreateAsset();
  const { mutate: deleteAsset } = useDeleteAsset();

  // Outstanding receivables (issued but unpaid client invoices)
  const { data: outstandingReceivables } = useOutstandingReceivables();

  // Tax liabilities & overpaid VAT
  const { data: vatOverpaid } = useVATOverpaid();
  const { data: allVATEntries = [] } = useVATEntries();
  const { data: vatCollectedSalda } = useVATCollected();
  const [showAssetForm, setShowAssetForm] = useState(false);
  const [assetForm, setAssetForm] = useState({
    name: "",
    value: "",
    as_of_date: new Date().toISOString().slice(0, 10),
    notes: "",
    company: "",
  });

  function saveAsset() {
    createAsset(
      {
        name: assetForm.name,
        value: assetForm.value,
        as_of_date: assetForm.as_of_date,
        notes: assetForm.notes,
        company: assetForm.company || null,
      },
      {
        onSuccess: () => {
          setShowAssetForm(false);
          setAssetForm((f) => ({
            ...f,
            name: "",
            value: "",
            notes: "",
            company: "",
          }));
        },
      },
    );
  }

  function startAdd(companyId: string) {
    setAddingFor(companyId);
    setForm((f) => ({
      ...f,
      company: companyId,
      date: new Date().toISOString().slice(0, 10),
      net_balance: "0",
      vat_balance: "0",
      notes: "",
    }));
  }

  function save() {
    createBalance(
      {
        company: form.company,
        date: form.date,
        net_balance: form.net_balance,
        vat_balance: form.vat_balance,
        notes: form.notes,
      },
      { onSuccess: () => setAddingFor(null) },
    );
  }

  const totalNet = latest.reduce(
    (s, b) => s + parseFloat(b.net_balance ?? "0"),
    0,
  );
  const totalVAT = latest.reduce(
    (s, b) => s + parseFloat(b.vat_balance ?? "0"),
    0,
  );
  const totalUnpaidTaxes =
    allVATEntries
      .filter(
        (e) =>
          !e.is_paid && (e.entry_type === "payable" || e.entry_type === "cit"),
      )
      .reduce((s, e) => s + parseFloat(e.amount), 0) +
    parseFloat(vatCollectedSalda?.total ?? "0");
  const vatOverpaidAmount = parseFloat(vatOverpaid?.total_vat_credited ?? "0");

  const totalLiquid = totalNet + totalVAT - totalUnpaidTaxes + totalFxPln;
  const totalNonLiquid =
    assets.reduce((s, a) => s + parseFloat(a.value), 0) + vatOverpaidAmount;
  const totalOutstandingReceivables = parseFloat(
    outstandingReceivables?.total_net ?? "0",
  );

  return (
    <div className="flex flex-col gap-6">
      {/* Summary strip */}
      <div className="grid grid-cols-2 gap-3">
        <div className="bg-white dark:bg-gray-800 rounded-lg p-3 sm:p-4 border border-gray-200 dark:border-gray-700">
          <p className="text-xs text-gray-500 dark:text-gray-400 font-medium uppercase tracking-wide mb-1">
            Saldo NETTO
          </p>
          <p className="text-sm sm:text-xl font-bold text-gray-800 dark:text-gray-100 whitespace-nowrap">
            {fmt(String(totalNet + totalFxPln))}
          </p>
          {totalFxPln > 0 && (
            <p className="text-[10px] text-blue-500 whitespace-nowrap mt-0.5">
              w tym waluty: {fmt(String(totalFxPln))} (kurs NBP)
            </p>
          )}
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg p-3 sm:p-4 border border-gray-200 dark:border-gray-700">
          <p className="text-xs text-gray-500 dark:text-gray-400 font-medium uppercase tracking-wide mb-1">
            Saldo VAT
          </p>
          <p className="text-sm sm:text-xl font-bold text-gray-800 dark:text-gray-100 whitespace-nowrap">
            {fmt(String(totalVAT))}
          </p>
        </div>
      </div>

      {/* Per-company table */}
      <div className="bg-white dark:bg-gray-800 overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-gray-50 dark:bg-gray-800/60 text-xs uppercase tracking-wide text-gray-400 border-b border-gray-200 dark:border-gray-700">
              <th className="text-left px-2 sm:px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">
                Spółka
              </th>
              <th className="text-right px-2 sm:px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400 whitespace-nowrap">
                Saldo netto
              </th>
              <th className="hidden sm:table-cell text-right px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400 whitespace-nowrap">
                Saldo VAT
              </th>
              <th className="hidden sm:table-cell text-right px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">
                Razem
              </th>
              <th className="hidden sm:table-cell text-left px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">
                Stan na
              </th>
              <th className="hidden sm:table-cell text-left px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">
                Zaktualizował
              </th>
              <th className="px-2 sm:px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400"></th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={7} className="px-4 py-6 text-center">
                  <div className="flex justify-center">
                    <Spinner />
                  </div>
                </td>
              </tr>
            ) : (
              latest.map((b) => {
                const companyFxPln = currencyAccounts
                  .filter((a) => a.company === b.company_id)
                  .reduce((s, a) => s + parseFloat(a.balance_pln ?? "0"), 0);
                const total =
                  parseFloat(b.net_balance ?? "0") +
                  parseFloat(b.vat_balance ?? "0") +
                  companyFxPln;
                const age = balanceAge(b.date);
                return (
                  <tr
                    key={b.company_id}
                    className="border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/30"
                  >
                    <td className="px-2 sm:px-4 py-3 font-semibold text-gray-800 dark:text-gray-200">
                      {b.company_code}
                      {age?.stale && (
                        <span
                          className={`block sm:hidden text-[10px] font-medium ${age.cls}`}
                          title={`Saldo z ${fmtDate(b.date)}`}
                        >
                          saldo {age.label}
                        </span>
                      )}
                    </td>
                    <td className="px-2 sm:px-4 py-3 text-right font-mono whitespace-nowrap">
                      <span className="text-[var(--color-primary)] dark:text-blue-300">
                        {fmt(b.net_balance)}
                      </span>
                      <span className="sm:hidden block text-purple-600 dark:text-purple-400 text-[10px] mt-0.5">
                        VAT {fmt(b.vat_balance)}
                      </span>
                    </td>
                    <td className="hidden sm:table-cell px-4 py-3 text-right font-mono text-purple-700 dark:text-purple-300 whitespace-nowrap">
                      {fmt(b.vat_balance)}
                    </td>
                    <td className="hidden sm:table-cell px-4 py-3 text-right font-mono font-semibold text-gray-800 dark:text-gray-200">
                      {fmt(String(total))}
                      {companyFxPln > 0 && (
                        <div className="text-[10px] font-normal text-blue-500">
                          +{fmt(String(companyFxPln))} FX
                        </div>
                      )}
                    </td>
                    <td className="hidden sm:table-cell px-4 py-3 text-gray-500">
                      {fmtDate(b.date)}
                      {age && (
                        <div className={`text-[11px] ${age.cls}`}>{age.label}</div>
                      )}
                    </td>
                    <td className="hidden sm:table-cell px-4 py-3 text-gray-400 text-xs">
                      {b.updated_by_name ?? "—"}
                    </td>
                    <td className="px-2 sm:px-4 py-3">
                      <button
                        onClick={() => startAdd(b.company_id)}
                        className="text-xs text-[var(--color-primary)] hover:underline whitespace-nowrap"
                      >
                        Aktualizuj
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Inline update form */}
      {addingFor && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-xl p-4 flex flex-col gap-3">
          <p className="text-sm font-semibold text-[var(--color-primary)] dark:text-blue-200">
            Aktualizacja salda:{" "}
            {latest.find((b) => b.company_id === addingFor)?.company_code}
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div>
              <label className="text-xs text-gray-500 block mb-1">Data</label>
              <DatePicker
                value={form.date}
                onChange={(v) =>
                  setForm((f) => ({ ...f, date: v }))
                }
              />
            </div>
            <div>
              <label className="text-xs text-gray-500 block mb-1">
                Saldo netto (PLN)
              </label>
              <input
                type="number"
                step="0.01"
                value={form.net_balance}
                onChange={(e) =>
                  setForm((f) => ({ ...f, net_balance: e.target.value }))
                }
                className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-2 py-1.5 text-sm bg-white dark:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]"
              />
            </div>
            <div>
              <label className="text-xs text-gray-500 block mb-1">
                Saldo VAT (PLN)
              </label>
              <input
                type="number"
                step="0.01"
                value={form.vat_balance}
                onChange={(e) =>
                  setForm((f) => ({ ...f, vat_balance: e.target.value }))
                }
                className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-2 py-1.5 text-sm bg-white dark:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]"
              />
            </div>
            <div>
              <label className="text-xs text-gray-500 block mb-1">Uwagi</label>
              <input
                type="text"
                value={form.notes}
                onChange={(e) =>
                  setForm((f) => ({ ...f, notes: e.target.value }))
                }
                className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-2 py-1.5 text-sm bg-white dark:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]"
              />
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={save}
              disabled={creating}
              className="px-4 py-1.5 bg-[var(--color-primary)] text-white text-sm rounded-lg hover:bg-[var(--color-primary-hover)] disabled:opacity-50"
            >
              {creating ? "Zapisywanie..." : "Zapisz"}
            </button>
            <button
              onClick={() => setAddingFor(null)}
              className="px-4 py-1.5 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700/50"
            >
              Anuluj
            </button>
          </div>

          {/* History for this company */}
          {history.length > 0 && (
            <div className="mt-2">
              <p className="text-xs text-gray-500 mb-1 font-medium">
                Historia zapisów:
              </p>
              <div className="flex flex-col gap-1">
                {history.slice(0, 5).map((h) => (
                  <div
                    key={h.id}
                    className="flex items-center gap-4 text-xs text-gray-500"
                  >
                    <span className="w-24">{fmtDate(h.date)}</span>
                    <span className="w-32 font-mono text-[var(--color-primary)]">
                      {fmt(h.net_balance)}
                    </span>
                    <span className="w-32 font-mono text-purple-600">
                      {fmt(h.vat_balance)}
                    </span>
                    <span className="text-gray-400">{h.updated_by_name}</span>
                    <button
                      onClick={() => deleteBalance(h.id)}
                      className="ml-auto text-red-400 hover:text-red-600"
                    >
                      <Trash2 size={12} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Non-liquid assets ─────────────────────────────────────────────── */}
      <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
            Majątek niepłynny
          </h3>
          {isCeo && (
            <button
              onClick={() => setShowAssetForm(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-[var(--color-primary)] text-white text-sm rounded-lg hover:bg-[var(--color-primary-hover)]"
            >
              <Plus size={14} /> Dodaj składnik
            </button>
          )}
        </div>

        {isCeo && showAssetForm && (
          <div className="bg-gray-50 dark:bg-gray-800/50 p-4 flex flex-col gap-3 mb-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="col-span-2">
                <label className="text-xs text-gray-500 block mb-1">
                  Nazwa składnika
                </label>
                <input
                  type="text"
                  value={assetForm.name}
                  onChange={(e) =>
                    setAssetForm((f) => ({ ...f, name: e.target.value }))
                  }
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-2 py-1.5 text-sm bg-white dark:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]"
                />
              </div>
              <div>
                <label className="text-xs text-gray-500 block mb-1">
                  Wartość netto (PLN)
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={assetForm.value}
                  onChange={(e) =>
                    setAssetForm((f) => ({ ...f, value: e.target.value }))
                  }
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-2 py-1.5 text-sm bg-white dark:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]"
                />
              </div>
              <div>
                <label className="text-xs text-gray-500 block mb-1">
                  Stan na
                </label>
                <DatePicker
                  value={assetForm.as_of_date}
                  onChange={(v) =>
                    setAssetForm((f) => ({ ...f, as_of_date: v }))
                  }
                />
              </div>
              <div>
                <label className="text-xs text-gray-500 block mb-1">
                  Spółka <span className="text-red-500">*</span>
                </label>
                <select
                  value={assetForm.company}
                  onChange={(e) =>
                    setAssetForm((f) => ({ ...f, company: e.target.value }))
                  }
                  className={`w-full border rounded-lg px-2 py-1.5 text-sm bg-white dark:bg-gray-700 ${!assetForm.company ? "border-red-400 dark:border-red-500" : "border-gray-300 dark:border-gray-600"}`}
                >
                  <option value="">— wybierz spółkę —</option>
                  {companies.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.short_code}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-span-2">
                <label className="text-xs text-gray-500 block mb-1">
                  Uwagi
                </label>
                <input
                  type="text"
                  value={assetForm.notes}
                  onChange={(e) =>
                    setAssetForm((f) => ({ ...f, notes: e.target.value }))
                  }
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-2 py-1.5 text-sm bg-white dark:bg-gray-700"
                />
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={saveAsset}
                disabled={
                  creatingAsset ||
                  !assetForm.name ||
                  !assetForm.value ||
                  !assetForm.company
                }
                className="px-4 py-1.5 bg-[var(--color-primary)] text-white text-sm rounded-lg hover:bg-[var(--color-primary-hover)] disabled:opacity-50"
              >
                {creatingAsset ? "Zapisywanie..." : "Zapisz"}
              </button>
              <button
                onClick={() => setShowAssetForm(false)}
                className="px-4 py-1.5 text-sm text-gray-600 dark:text-gray-400 "
              >
                Anuluj
              </button>
            </div>
          </div>
        )}

        <div className="bg-white dark:bg-gray-800 overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-gray-50 dark:bg-gray-800/60 text-xs uppercase tracking-wide text-gray-400 border-b border-gray-200 dark:border-gray-700">
                <th className="text-left px-2 sm:px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">
                  Składnik
                </th>
                <th className="text-left px-2 sm:px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">
                  Spółka
                </th>
                <th className="text-right px-2 sm:px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">
                  <span className="hidden sm:inline">Wartość </span>Netto
                </th>
                <th className="hidden sm:table-cell text-right px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">
                  Wartość brutto
                </th>
                <th className="hidden sm:table-cell text-left px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">
                  Stan na
                </th>
                <th className="hidden sm:table-cell text-left px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">
                  Uwagi
                </th>
                <th className="hidden sm:table-cell text-left px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">
                  Dodał
                </th>
                {isCeo && (
                  <th className="px-2 sm:px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400"></th>
                )}
              </tr>
            </thead>
            <tbody>
              {assets.length === 0 ? (
                <tr>
                  <td
                    colSpan={isCeo ? 8 : 7}
                    className="px-4 py-6 text-center text-gray-400 text-sm"
                  >
                    Brak składników majątku niepłynnego.
                  </td>
                </tr>
              ) : (
                assets.map((a) => (
                  <tr
                    key={a.id}
                    className="border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/30"
                  >
                    <td className="px-2 sm:px-4 py-2 font-medium text-gray-800 dark:text-gray-200">
                      {a.name}
                    </td>
                    <td className="px-2 sm:px-4 py-2 text-gray-400 text-xs">
                      {a.company_code ?? "—"}
                    </td>
                    <td className="px-2 sm:px-4 py-2 text-right font-mono text-gray-800 dark:text-gray-200 whitespace-nowrap">
                      {fmt(a.value)}
                    </td>
                    <td className="hidden sm:table-cell px-4 py-2 text-right font-mono text-gray-500 dark:text-gray-400 text-xs">
                      {fmt(
                        String(
                          Math.round(parseFloat(a.value) * 1.23 * 100) / 100,
                        ),
                      )}
                    </td>
                    <td className="hidden sm:table-cell px-4 py-2 text-gray-500">
                      {fmtDate(a.as_of_date)}
                    </td>
                    <td className="hidden sm:table-cell px-4 py-2 text-gray-400 text-xs">
                      {a.notes || "—"}
                    </td>
                    <td className="hidden sm:table-cell px-4 py-2 text-gray-400 text-xs">
                      {a.created_by_name ?? "—"}
                    </td>
                    {isCeo && (
                      <td className="px-2 sm:px-4 py-2">
                        <button
                          onClick={() => deleteAsset(a.id)}
                          className="text-red-400 hover:text-red-600"
                        >
                          <Trash2 size={14} />
                        </button>
                      </td>
                    )}
                  </tr>
                ))
              )}
              {vatOverpaidAmount > 0 &&
                (() => {
                  // Group VAT overpaid invoices by company
                  const byCompany = new Map<
                    string,
                    { code: string | null; vat: number; count: number }
                  >();
                  for (const inv of vatOverpaid?.invoices ?? []) {
                    const key = inv.company_id ?? "__none__";
                    const existing = byCompany.get(key);
                    if (existing) {
                      existing.vat += parseFloat(inv.vat_credited);
                      existing.count += 1;
                    } else {
                      byCompany.set(key, {
                        code: inv.company_code,
                        vat: parseFloat(inv.vat_credited),
                        count: 1,
                      });
                    }
                  }
                  return Array.from(byCompany.entries()).map(([key, group]) => {
                    const n = group.count;
                    const s =
                      n === 1
                        ? "faktura"
                        : n >= 2 && n <= 4
                          ? "faktury"
                          : "faktur";
                    return (
                      <tr
                        key={key}
                        className="border-b border-amber-100 bg-amber-50/40 dark:bg-amber-900/10"
                      >
                        <td className="px-2 sm:px-4 py-2 font-medium text-amber-800 dark:text-amber-300">
                          <button
                            onClick={() => setSearchParams({ tab: "vat" })}
                            className="underline hover:no-underline text-left"
                          >
                            VAT nadpłacony (zapłacony za nieopłacone FV PRZ)
                          </button>
                        </td>
                        <td className="px-2 sm:px-4 py-2 text-amber-700 dark:text-amber-400 text-xs font-medium">
                          {group.code ?? "—"}
                        </td>
                        <td className="px-2 sm:px-4 py-2 text-right font-mono text-amber-800 dark:text-amber-300 whitespace-nowrap">
                          {fmt(String(group.vat))}
                        </td>
                        <td className="hidden sm:table-cell px-4 py-2 text-right font-mono text-gray-400 text-xs">
                          —
                        </td>
                        <td className="hidden sm:table-cell px-4 py-2 text-gray-400 text-xs">
                          —
                        </td>
                        <td
                          className="hidden sm:table-cell px-4 py-2 text-amber-600 dark:text-amber-400 text-xs"
                          colSpan={2}
                        >
                          {n} {s} — do odzyskania po opłaceniu przez inwestorów
                        </td>
                        {isCeo && (
                          <td className="px-4 py-2 text-gray-300 text-xs">
                            auto
                          </td>
                        )}
                      </tr>
                    );
                  });
                })()}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Combined total ────────────────────────────────────────────────── */}
      <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-2 sm:p-4 border border-gray-200 dark:border-gray-700 min-w-0">
            <p className="text-[9px] sm:text-xs text-gray-500 dark:text-gray-400 font-medium uppercase tracking-wide mb-1 leading-tight">
              Majątek płynny
            </p>
            <p className="text-[11px] sm:text-lg font-bold text-gray-800 dark:text-gray-100 leading-snug break-words">
              {fmt(String(totalLiquid))}
            </p>
            {totalUnpaidTaxes > 0 && (
              <p className="text-[9px] sm:text-xs text-orange-500 mt-1 leading-tight">
                Netto + VAT − {fmt(String(totalUnpaidTaxes))} VAT/CIT
              </p>
            )}
            {totalFxPln > 0 && (
              <p className="text-[9px] sm:text-xs text-blue-500 mt-0.5 leading-tight">
                w tym waluty: {fmt(String(totalFxPln))} (kurs NBP)
              </p>
            )}
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg p-2 sm:p-4 border border-gray-200 dark:border-gray-700 min-w-0">
            <p className="text-[9px] sm:text-xs text-gray-500 dark:text-gray-400 font-medium uppercase tracking-wide mb-1 leading-tight">
              Majątek niepłynny
            </p>
            <p className="text-[11px] sm:text-lg font-bold text-gray-800 dark:text-gray-100 leading-snug break-words">
              {fmt(String(totalNonLiquid))}
            </p>
            {vatOverpaidAmount > 0 && (
              <p className="text-[9px] sm:text-xs text-amber-600 dark:text-amber-400 mt-1 leading-tight">
                W tym VAT nadpłacony: {fmt(String(vatOverpaidAmount))}
              </p>
            )}
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg p-2 sm:p-4 border border-gray-200 dark:border-gray-700 min-w-0">
            <p className="text-[9px] sm:text-xs text-gray-500 dark:text-gray-400 font-medium uppercase tracking-wide mb-1 leading-tight">
              Pozostałe FV do zapłaty
            </p>
            <p className="text-[11px] sm:text-lg font-bold text-blue-700 dark:text-blue-400 leading-snug break-words">
              {fmt(String(totalOutstandingReceivables))}
            </p>
            <p className="text-[9px] sm:text-xs text-gray-400 mt-1 leading-tight hidden sm:block">
              Netto — wystawione, nieopłacone
            </p>
          </div>
          <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-2 sm:p-4 border border-gray-200 dark:border-gray-600 min-w-0">
            <p className="text-[9px] sm:text-xs text-gray-500 dark:text-gray-400 font-medium uppercase tracking-wide mb-1 leading-tight">
              Razem GK
            </p>
            <p className="text-[11px] sm:text-lg font-bold text-gray-900 dark:text-gray-50 leading-snug break-words">
              {fmt(String(totalLiquid + totalNonLiquid + totalOutstandingReceivables))}
            </p>
            <p className="text-[9px] sm:text-xs text-gray-400 mt-1 leading-tight hidden sm:block">
              Płynny + niepłynny + należności
            </p>
          </div>
        </div>
      </div>

      {/* Balance history chart */}
      <BalanceHistoryChart checkpoints={balanceHistory} />

      {/* Foreign currency accounts */}
      <div className="border-t border-[var(--color-border)] pt-6">
        <CurrencyAccountsPanel isCeo={isCeo} />
      </div>
    </div>
  );
}

// ── Tab: Plan finansowy GK ─────────────────────────────────────────────────

function EntryRow({
  entry,
  onDelete,
}: {
  entry: GroupPlannedEntry;
  onDelete: () => void;
}) {
  return (
    <tr className="border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/30">
      <td className="px-4 py-2 text-gray-800 dark:text-gray-200">
        {entry.name}
      </td>
      <td className="px-4 py-2">
        <span
          className={`text-xs font-semibold px-2 py-0.5 rounded-full ${entry.entry_type === "revenue" ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}
        >
          {entry.entry_type_display}
        </span>
      </td>
      <td className="px-4 py-2 text-right font-mono text-gray-800 dark:text-gray-200">
        {fmt(entry.amount)}
      </td>
      <td className="px-4 py-2 text-gray-500">
        {fmtDate(entry.expected_date)}
      </td>
      <td className="px-4 py-2 text-gray-400 text-xs">
        {entry.is_recurring
          ? `Cyklicznie co ${entry.recurrence_months ?? "?"} mies.`
          : "Jednorazowo"}
      </td>
      <td className="px-4 py-2 text-gray-400 text-xs">{entry.notes}</td>
      <td className="px-4 py-2">
        <button onClick={onDelete} className="text-red-400 hover:text-red-600">
          <Trash2 size={14} />
        </button>
      </td>
    </tr>
  );
}

function PlanTab() {
  const { data: entries = [] } = usePlannedEntries();
  const { mutate: create, isPending } = useCreatePlannedEntry();
  const { mutate: del } = useDeletePlannedEntry();
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    entry_type: "revenue" as "cost" | "revenue",
    name: "",
    amount: "",
    expected_date: new Date().toISOString().slice(0, 10),
    is_recurring: false,
    recurrence_months: "",
    recurrence_end_date: "",
    notes: "",
  });

  function save() {
    create(
      {
        entry_type: form.entry_type,
        name: form.name,
        amount: form.amount,
        expected_date: form.expected_date,
        is_recurring: form.is_recurring,
        recurrence_months:
          form.is_recurring && form.recurrence_months
            ? parseInt(form.recurrence_months)
            : null,
        recurrence_end_date:
          form.is_recurring && form.recurrence_end_date
            ? form.recurrence_end_date
            : null,
        notes: form.notes,
      },
      {
        onSuccess: () => {
          setShowForm(false);
          setForm((f) => ({ ...f, name: "", amount: "", notes: "" }));
        },
      },
    );
  }

  const revenues = entries.filter((e) => e.entry_type === "revenue");
  const costs = entries.filter((e) => e.entry_type === "cost");
  const totalRev = revenues.reduce((s, e) => s + parseFloat(e.amount), 0);
  const totalCost = costs.reduce((s, e) => s + parseFloat(e.amount), 0);

  return (
    <div className="flex flex-col gap-6">
      <div className="grid grid-cols-3 gap-2 sm:gap-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg p-2 sm:p-4 border border-gray-200 dark:border-gray-700 min-w-0">
          <p className="text-[9px] sm:text-xs text-gray-500 dark:text-gray-400 font-medium uppercase tracking-wide mb-1 leading-tight">
            Przychody
          </p>
          <p className="text-[11px] sm:text-xl font-bold text-gray-800 dark:text-gray-100 leading-snug break-words">
            {fmt(String(totalRev))}
          </p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg p-2 sm:p-4 border border-gray-200 dark:border-gray-700 min-w-0">
          <p className="text-[9px] sm:text-xs text-gray-500 dark:text-gray-400 font-medium uppercase tracking-wide mb-1 leading-tight">
            Koszty
          </p>
          <p className="text-[11px] sm:text-xl font-bold text-gray-800 dark:text-gray-100 leading-snug break-words">
            {fmt(String(totalCost))}
          </p>
        </div>
        <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-2 sm:p-4 border border-gray-200 dark:border-gray-600 min-w-0">
          <p className="text-[9px] sm:text-xs text-gray-500 dark:text-gray-400 font-medium uppercase tracking-wide mb-1 leading-tight">
            Bilans
          </p>
          <p
            className={`text-[11px] sm:text-xl font-bold leading-snug break-words ${totalRev - totalCost >= 0 ? "text-gray-800 dark:text-gray-100" : "text-red-600 dark:text-red-400"}`}
          >
            {fmt(String(totalRev - totalCost))}
          </p>
        </div>
      </div>

      <div className="flex justify-between items-center">
        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
          Wpisy planu finansowego GK
        </h3>
        <button
          onClick={() => setShowForm(true)}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-[var(--color-primary)] text-white text-sm rounded-lg hover:bg-[var(--color-primary-hover)]"
        >
          <Plus size={14} /> Dodaj wpis
        </button>
      </div>

      {showForm && (
        <div className="bg-gray-50 dark:bg-gray-800/50 p-4 flex flex-col gap-3">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div>
              <label className="text-xs text-gray-500 block mb-1">Typ</label>
              <select
                value={form.entry_type}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    entry_type: e.target.value as "cost" | "revenue",
                  }))
                }
                className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-2 py-1.5 text-sm bg-white dark:bg-gray-700"
              >
                <option value="revenue">Przychód</option>
                <option value="cost">Koszt</option>
              </select>
            </div>
            <div className="col-span-2">
              <label className="text-xs text-gray-500 block mb-1">Nazwa</label>
              <input
                type="text"
                value={form.name}
                onChange={(e) =>
                  setForm((f) => ({ ...f, name: e.target.value }))
                }
                className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-2 py-1.5 text-sm bg-white dark:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]"
              />
            </div>
            <div>
              <label className="text-xs text-gray-500 block mb-1">
                Kwota (PLN)
              </label>
              <input
                type="number"
                step="0.01"
                value={form.amount}
                onChange={(e) =>
                  setForm((f) => ({ ...f, amount: e.target.value }))
                }
                className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-2 py-1.5 text-sm bg-white dark:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]"
              />
            </div>
            <div>
              <label className="text-xs text-gray-500 block mb-1">
                Data oczekiwana
              </label>
              <DatePicker
                value={form.expected_date}
                onChange={(v) =>
                  setForm((f) => ({ ...f, expected_date: v }))
                }
              />
            </div>
            <div className="flex items-center gap-2 pt-4">
              <input
                type="checkbox"
                id="recurring"
                checked={form.is_recurring}
                onChange={(e) =>
                  setForm((f) => ({ ...f, is_recurring: e.target.checked }))
                }
                className="rounded"
              />
              <label
                htmlFor="recurring"
                className="text-sm text-gray-600 dark:text-gray-400"
              >
                Cykliczny
              </label>
            </div>
            {form.is_recurring && (
              <>
                <div>
                  <label className="text-xs text-gray-500 block mb-1">
                    Co ile miesięcy
                  </label>
                  <input
                    type="number"
                    value={form.recurrence_months}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        recurrence_months: e.target.value,
                      }))
                    }
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-2 py-1.5 text-sm bg-white dark:bg-gray-700"
                  />
                </div>
                <div>
                  <label className="text-xs text-gray-500 block mb-1">
                    Do kiedy
                  </label>
                  <DatePicker
                    value={form.recurrence_end_date}
                    onChange={(v) =>
                      setForm((f) => ({
                        ...f,
                        recurrence_end_date: v,
                      }))
                    }
                  />
                </div>
              </>
            )}
            <div className="col-span-2">
              <label className="text-xs text-gray-500 block mb-1">Uwagi</label>
              <input
                type="text"
                value={form.notes}
                onChange={(e) =>
                  setForm((f) => ({ ...f, notes: e.target.value }))
                }
                className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-2 py-1.5 text-sm bg-white dark:bg-gray-700"
              />
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={save}
              disabled={isPending || !form.name || !form.amount}
              className="px-4 py-1.5 bg-[var(--color-primary)] text-white text-sm rounded-lg hover:bg-[var(--color-primary-hover)] disabled:opacity-50"
            >
              {isPending ? "Zapisywanie..." : "Zapisz"}
            </button>
            <button
              onClick={() => setShowForm(false)}
              className="px-4 py-1.5 text-sm text-gray-600 dark:text-gray-400 "
            >
              Anuluj
            </button>
          </div>
        </div>
      )}

      <div className="bg-white dark:bg-gray-800 overflow-x-auto">
        <table className="w-full text-xs min-w-[400px]">
          <thead>
            <tr className="bg-gray-50 dark:bg-gray-800/60 text-xs uppercase tracking-wide text-gray-400 border-b border-gray-200 dark:border-gray-700">
              <th className="text-left px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">
                Nazwa
              </th>
              <th className="text-left px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">
                Typ
              </th>
              <th className="text-right px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">
                Kwota
              </th>
              <th className="text-left px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">
                Data
              </th>
              <th className="text-left px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">
                Powtarzalność
              </th>
              <th className="text-left px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">
                Uwagi
              </th>
              <th className="px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400"></th>
            </tr>
          </thead>
          <tbody>
            {entries.length === 0 ? (
              <tr>
                <td
                  colSpan={7}
                  className="px-4 py-6 text-center text-gray-400 text-sm"
                >
                  Brak wpisów.
                </td>
              </tr>
            ) : (
              entries.map((e) => (
                <EntryRow key={e.id} entry={e} onDelete={() => del(e.id)} />
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Tab: VAT / CIT ─────────────────────────────────────────────────────────

type VATEntryType = "carryforward" | "refund" | "payable" | "cit";

const VAT_TYPE_LABELS: Record<VATEntryType, string> = {
  carryforward: "VAT spłynięty",
  refund: "Zwrot VAT",
  payable: "VAT do zapłaty",
  cit: "CIT / zaliczka CIT",
};

const VAT_TYPE_COLORS: Record<VATEntryType, string> = {
  carryforward: "bg-yellow-100 text-yellow-700",
  refund: "bg-green-100 text-green-700",
  payable: "bg-orange-100 text-orange-700",
  cit: "bg-purple-100 text-purple-700",
};

// ── "VAT spłynięty" detail ──────────────────────────────────────────────────
// The table mixes output VAT (our invoices to investors) with input VAT
// (cost invoices), so every row states its direction outright instead of
// leaning on the sign of the amount alone. Dates are TAX dates — for an
// advance invoice the VAT falls in the month of the investor's payment
// (art. 19a ust. 8), not the month it was issued, and the issue date is shown
// underneath so an invoice written in July but paid in August reads correctly.

type VATCollectedInvoice = NonNullable<
  ReturnType<typeof useVATCollected>["data"]
>["by_company"][number]["invoices"][number];

const DIRECTION_BADGE: Record<"income" | "cost", { cls: string; label: string }> = {
  income: {
    cls: "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-800",
    label: "Przychodowa",
  },
  cost: {
    cls: "bg-red-50 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-800",
    label: "Kosztowa",
  },
};

const PAYMENT_BASIS_LABELS: Record<string, string> = {
  gross: "brutto",
  net: "tylko netto",
  partial: "częściowo",
  none: "nieopłacona",
};

function DirectionBadge({ direction }: { direction: "income" | "cost" }) {
  const cfg = DIRECTION_BADGE[direction];
  return (
    <span className={`inline-block whitespace-nowrap px-2.5 py-1 rounded-full border text-[11px] font-semibold ${cfg.cls}`}>
      {cfg.label}
    </span>
  );
}

function BasisBadge({ basis }: { basis: string }) {
  const cls =
    basis === "gross"
      ? "text-emerald-700 dark:text-emerald-400"
      : basis === "none"
        ? "text-gray-400 dark:text-gray-500"
        : "text-amber-700 dark:text-amber-400";
  return (
    <span className={`text-[11px] font-medium whitespace-nowrap ${cls}`}>
      {PAYMENT_BASIS_LABELS[basis] ?? basis}
    </span>
  );
}

function vatCollectedStatusBadge(status: string) {
  const cfg =
    status === "paid"
      ? { cls: "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400", label: "Opłacona" }
      : status === "partially_paid"
        ? { cls: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-400", label: "Częściowo" }
        : status === "transfer_prepared"
          ? { cls: "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-400", label: "Przelew przyg." }
          : { cls: "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300", label: "Nieopłacona" };
  return (
    <span className={`inline-block whitespace-nowrap px-2.5 py-1 rounded-full text-[11px] font-semibold ${cfg.cls}`}>
      {cfg.label}
    </span>
  );
}

/** Signed VAT amount — "+" for output VAT, "−" for deductible input VAT. */
function signedVAT(value: string) {
  const v = parseFloat(value);
  return {
    text: `${v < 0 ? "−" : "+"}${fmt(String(Math.abs(v)))}`,
    cls: v < 0 ? "text-red-600 dark:text-red-400" : "text-emerald-700 dark:text-emerald-400",
  };
}

function TaxDateCell({ inv }: { inv: VATCollectedInvoice }) {
  const issuedDiffers = inv.invoice_date && inv.invoice_date !== inv.tax_date;
  return (
    <div className="flex flex-col gap-0.5">
      <span className="whitespace-nowrap text-gray-700 dark:text-gray-300">{fmtDate(inv.tax_date)}</span>
      {issuedDiffers && (
        <span className="whitespace-nowrap text-[11px] text-gray-400 dark:text-gray-500">
          {inv.is_advance_invoice ? "zaliczkowa, wyst. " : "wyst. "}
          {fmtDate(inv.invoice_date)}
        </span>
      )}
    </div>
  );
}

function VATCollectedDetail({
  vatCollected,
  isMobile,
  onClose,
}: {
  vatCollected: NonNullable<ReturnType<typeof useVATCollected>["data"]>;
  isMobile: boolean;
  onClose: () => void;
}) {
  const allInvoices = vatCollected.by_company
    .flatMap((c) => c.invoices)
    .slice()
    .sort((a, b) => (a.tax_date < b.tax_date ? 1 : a.tax_date > b.tax_date ? -1 : 0));
  const otherCosts = (vatCollected.other_costs?.count ?? 0) > 0 ? vatCollected.other_costs : null;

  const incomeVAT = allInvoices
    .filter((i) => i.direction === "income")
    .reduce((s, i) => s + parseFloat(i.vat_received), 0);
  const costVAT =
    allInvoices.filter((i) => i.direction === "cost").reduce((s, i) => s + parseFloat(i.vat_received), 0) -
    parseFloat(otherCosts?.vat ?? "0");
  // The card above states the same figure as a cash effect (a VAT liability shown
  // as money going out), so the sign alone would read as a contradiction here.
  const rawTotal = parseFloat(vatCollected.total);
  const saldoNote =
    rawTotal > 0 ? "VAT do zapłaty do US" : rawTotal < 0 ? "nadwyżka VAT naliczonego" : "";

  const summary = (
    <div className="flex flex-wrap gap-3">
      <div className="rounded-lg border border-gray-200 dark:border-gray-700 px-4 py-2.5 min-w-[15rem]">
        <div className="text-[11px] uppercase tracking-wide text-gray-400 mb-1">
          VAT należny — faktury przychodowe
        </div>
        <div className="font-mono text-base font-semibold text-emerald-700 dark:text-emerald-400">
          +{fmt(String(incomeVAT))}
        </div>
      </div>
      <div className="rounded-lg border border-gray-200 dark:border-gray-700 px-4 py-2.5 min-w-[15rem]">
        <div className="text-[11px] uppercase tracking-wide text-gray-400 mb-1">
          VAT naliczony — faktury kosztowe
        </div>
        <div className="font-mono text-base font-semibold text-red-600 dark:text-red-400">
          −{fmt(String(Math.abs(costVAT)))}
        </div>
      </div>
      <div className="rounded-lg border border-gray-200 dark:border-gray-700 px-4 py-2.5 min-w-[15rem]">
        <div className="text-[11px] uppercase tracking-wide text-gray-400 mb-1">
          Saldo do rozliczenia{saldoNote ? ` — ${saldoNote}` : ""}
        </div>
        <div className="font-mono text-base font-semibold text-gray-900 dark:text-gray-100">
          {fmt(vatCollected.total)}
        </div>
      </div>
    </div>
  );

  const periodNote = (
    <p className="text-xs leading-relaxed text-gray-400 dark:text-gray-500 max-w-4xl">
      Daty to daty obowiązku podatkowego — przy fakturach zaliczkowych liczy się data wpłaty
      (art. 19a ust. 8), nie data wystawienia. Okresy rozliczone zapisem „VAT do zapłaty" nie są
      tu ujmowane.
    </p>
  );

  if (isMobile) {
    return (
      <Modal open title="VAT spłynięty" onClose={onClose} size="md">
        <div className="flex flex-col gap-3">
          {summary}
          {periodNote}
          {allInvoices.map((inv) => (
            <div key={inv.invoice_id} className="rounded-lg border border-gray-200 dark:border-gray-700 p-3 flex flex-col gap-2">
              <div className="flex items-start justify-between gap-2">
                <span className="font-mono font-semibold text-sm text-gray-900 dark:text-gray-100 break-all">
                  {inv.invoice_number}
                </span>
                <DirectionBadge direction={inv.direction} />
              </div>
              <div className="flex items-start justify-between gap-2 text-xs text-gray-500 dark:text-gray-400">
                <span>{inv.project_name}</span>
                <TaxDateCell inv={inv} />
              </div>
              <div className="grid grid-cols-3 gap-2 pt-1 border-t border-gray-100 dark:border-gray-800">
                <div className="flex flex-col gap-0.5">
                  <span className="text-[10px] uppercase tracking-wide text-gray-400">Netto</span>
                  <span className="font-mono text-xs text-gray-700 dark:text-gray-300 whitespace-nowrap">{fmt(inv.amount_net)}</span>
                </div>
                <div className="flex flex-col gap-0.5">
                  <span className="text-[10px] uppercase tracking-wide text-gray-400">Brutto</span>
                  <span className="font-mono text-xs text-gray-700 dark:text-gray-300 whitespace-nowrap">{fmt(inv.amount_gross)}</span>
                </div>
                <div className="flex flex-col gap-0.5 text-right">
                  <span className="text-[10px] uppercase tracking-wide text-gray-400">VAT</span>
                  <span className={`font-mono text-xs font-bold whitespace-nowrap ${signedVAT(inv.vat_received).cls}`}>
                    {signedVAT(inv.vat_received).text}
                  </span>
                </div>
              </div>
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-baseline gap-2">
                  <span className="text-[10px] uppercase tracking-wide text-gray-400">Zapłacono</span>
                  <span className="font-mono text-xs text-gray-700 dark:text-gray-300 whitespace-nowrap">{fmt(inv.paid_amount)}</span>
                  <BasisBadge basis={inv.payment_basis} />
                </div>
                {vatCollectedStatusBadge(inv.payment_status)}
              </div>
            </div>
          ))}
          {otherCosts && (
            <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3 flex flex-col gap-2 bg-gray-50 dark:bg-gray-800/50">
              <div className="flex items-start justify-between gap-2">
                <span className="font-semibold text-sm text-gray-900 dark:text-gray-100">
                  FV inne (kosztowe) — {otherCosts.count} szt.
                </span>
                <DirectionBadge direction="cost" />
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400">
                Faktury kosztowe pracowników, PLN, bez kilometrówek — VAT 23% liczony z brutto
              </div>
              <div className="grid grid-cols-3 gap-2 pt-1 border-t border-gray-100 dark:border-gray-800">
                <div className="flex flex-col gap-0.5">
                  <span className="text-[10px] uppercase tracking-wide text-gray-400">Netto</span>
                  <span className="font-mono text-xs text-gray-700 dark:text-gray-300 whitespace-nowrap">{fmt(otherCosts.net_total)}</span>
                </div>
                <div className="flex flex-col gap-0.5">
                  <span className="text-[10px] uppercase tracking-wide text-gray-400">Brutto</span>
                  <span className="font-mono text-xs text-gray-700 dark:text-gray-300 whitespace-nowrap">{fmt(otherCosts.gross_total)}</span>
                </div>
                <div className="flex flex-col gap-0.5 text-right">
                  <span className="text-[10px] uppercase tracking-wide text-gray-400">VAT</span>
                  <span className="font-mono text-xs font-bold text-red-600 dark:text-red-400 whitespace-nowrap">
                    −{fmt(otherCosts.vat)}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>
      </Modal>
    );
  }

  const cell = "py-4 pr-5 align-middle";
  const tight = `${cell} w-px whitespace-nowrap`;
  const numCell = `${tight} text-right font-mono tabular-nums`;
  const head =
    "pb-3 pr-5 font-mono text-[11px] uppercase tracking-widest text-gray-400 dark:text-gray-500 whitespace-nowrap";

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 flex flex-col gap-5">
      <div className="flex items-start justify-between gap-4">
        <div className="flex flex-col gap-3">
          <h3 className="text-base font-semibold text-gray-700 dark:text-gray-300">
            VAT spłynięty — faktury nierozliczone jeszcze z US
          </h3>
          {summary}
          {periodNote}
        </div>
        <button
          onClick={onClose}
          className="text-sm text-gray-400 hover:text-gray-600 underline shrink-0"
        >
          Ukryj
        </button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[1040px] text-sm border-collapse">
          <thead>
            <tr className="border-b border-gray-200 dark:border-gray-700">
              <th className={`text-left ${head} w-px`}>Nr faktury</th>
              <th className={`text-left ${head} w-px`}>Rodzaj</th>
              <th className={`text-left ${head} w-px`}>Data VAT</th>
              <th className={`text-left ${head} w-full`}>Przedsięwzięcie</th>
              <th className={`text-right ${head}`}>Netto</th>
              <th className={`text-right ${head}`}>Brutto</th>
              <th className={`text-right ${head}`}>Zapłacono</th>
              <th className={`text-right ${head}`}>VAT</th>
              <th className={`text-left ${head} w-px pr-0`}>Status</th>
            </tr>
          </thead>
          <tbody>
            {allInvoices.map((inv) => {
              const vat = signedVAT(inv.vat_received);
              return (
                <tr
                  key={inv.invoice_id}
                  className="border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors"
                >
                  <td className={`${tight} font-mono font-medium text-gray-800 dark:text-gray-200`}>
                    {inv.invoice_number}
                  </td>
                  <td className={tight}>
                    <DirectionBadge direction={inv.direction} />
                  </td>
                  <td className={`${tight} text-gray-500 dark:text-gray-400`}>
                    <TaxDateCell inv={inv} />
                  </td>
                  <td className={`${cell} text-gray-600 dark:text-gray-300`}>{inv.project_name}</td>
                  <td className={`${numCell} text-gray-500 dark:text-gray-400`}>{fmt(inv.amount_net)}</td>
                  <td className={`${numCell} text-gray-600 dark:text-gray-300`}>{fmt(inv.amount_gross)}</td>
                  <td className={numCell}>
                    <div className="flex flex-col items-end gap-1">
                      <span className="text-gray-700 dark:text-gray-300">{fmt(inv.paid_amount)}</span>
                      <BasisBadge basis={inv.payment_basis} />
                    </div>
                  </td>
                  <td className={`${numCell} font-semibold ${vat.cls}`}>{vat.text}</td>
                  <td className={`${tight} pr-0`}>{vatCollectedStatusBadge(inv.payment_status)}</td>
                </tr>
              );
            })}
            {otherCosts && (
              <tr className="border-b border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50">
                <td className={`${tight} font-medium text-gray-800 dark:text-gray-200`}>
                  FV inne ({otherCosts.count} szt.)
                </td>
                <td className={tight}>
                  <DirectionBadge direction="cost" />
                </td>
                <td className={`${tight} text-gray-400`}>—</td>
                <td className={`${cell} text-gray-600 dark:text-gray-300 leading-relaxed`}>
                  FV kosztowe pracowników — PLN, bez kilometrówek, VAT 23%
                </td>
                <td className={`${numCell} text-gray-500 dark:text-gray-400`}>{fmt(otherCosts.net_total)}</td>
                <td className={`${numCell} text-gray-600 dark:text-gray-300`}>{fmt(otherCosts.gross_total)}</td>
                <td className={`${numCell} text-gray-400`}>—</td>
                <td className={`${numCell} font-semibold text-red-600 dark:text-red-400`}>−{fmt(otherCosts.vat)}</td>
                <td className={`${tight} pr-0 text-gray-400`}>—</td>
              </tr>
            )}
          </tbody>
          <tfoot>
            <tr className="border-t-2 border-gray-200 dark:border-gray-700">
              <td className={`${cell} font-semibold text-gray-700 dark:text-gray-300`} colSpan={7}>
                Saldo VAT do rozliczenia z US{saldoNote ? ` — ${saldoNote}` : ""}
              </td>
              <td className={`${numCell} font-bold text-gray-900 dark:text-gray-100`}>{fmt(vatCollected.total)}</td>
              <td className={`${tight} pr-0`} />
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}

function VATTab() {
  const { data: entries = [] } = useVATEntries();
  const { mutate: create, isPending } = useCreateVATEntry();
  const { mutate: del } = useDeleteVATEntry();
  const { data: overpaid } = useVATOverpaid();
  const { data: vatCollected } = useVATCollected();
  const { data: companies = [] } = useCompanies();
  const [showForm, setShowForm] = useState(false);
  const [showOverpaidDetail, setShowOverpaidDetail] = useState(false);
  const [showCollectedDetail, setShowCollectedDetail] = useState(false);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 640);
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);
  const [form, setForm] = useState({
    entry_type: "payable" as VATEntryType,
    company: "",
    amount: "",
    period:
      (() => {
        const d = new Date();
        d.setMonth(d.getMonth() - 1);
        return d.toISOString().slice(0, 7);
      })() + "-01",
    description: "",
    invoice_ref: "",
  });

  // Auto-suggestion for payable VAT
  const suggestionPeriod = form.entry_type === "payable" ? form.period : null;
  const { data: suggestion } = useVATSuggestion(suggestionPeriod, form.company || null);
  // Auto-suggestion for CIT: net revenue in selected period
  const citSuggestionPeriod = form.entry_type === "cit" ? form.period : null;
  const { data: citSuggestion } = useCITSuggestion(citSuggestionPeriod);
  const { mutate: markPaid } = useMarkVATEntryPaid();
  const today = new Date().toISOString().split("T")[0];
  const [pendingPaidId, setPendingPaidId] = useState<string | null>(null);
  const [pendingPaidDate, setPendingPaidDate] = useState(today);

  function save() {
    create(
      {
        entry_type: form.entry_type,
        company: form.company || null,
        amount: form.amount,
        period: form.period,
        description: form.description,
        invoice_ref: form.invoice_ref,
      },
      {
        onSuccess: () => {
          setShowForm(false);
          setForm((f) => ({
            ...f,
            amount: "",
            description: "",
            invoice_ref: "",
          }));
        },
      },
    );
  }

  const sum = (type: VATEntryType) =>
    entries
      .filter((e) => e.entry_type === type && !e.is_paid)
      .reduce((s, e) => s + parseFloat(e.amount), 0);
  const totalVATCredited = parseFloat(overpaid?.total_vat_credited ?? "0");

  return (
    <div className="flex flex-col gap-6">
      {/* Auto-calculated overpaid VAT from invoices */}
      <div className="bg-white dark:bg-gray-800 border-l-4 border-l-amber-400 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs text-gray-500 dark:text-gray-400 font-medium uppercase tracking-wide mb-1">
              Nadpłacony VAT (automatycznie)
            </p>
            <p className="text-xl sm:text-2xl font-bold text-gray-800 dark:text-gray-100">
              {fmt(String(totalVATCredited))}
            </p>
            <p className="text-xs text-gray-400 mt-1">
              VAT zakredytowany do US z wystawionych faktur, za które inwestor
              jeszcze nie zapłacił.
              {overpaid &&
                overpaid.invoices.length > 0 &&
                ` Dotyczy ${overpaid.invoices.length} ${overpaid.invoices.length === 1 ? "faktury" : overpaid.invoices.length >= 2 && overpaid.invoices.length <= 4 ? "faktur" : "faktur"}.`}
            </p>
          </div>
          {overpaid && overpaid.invoices.length > 0 && (
            <button
              onClick={() => setShowOverpaidDetail((v) => !v)}
              className="text-xs text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 underline ml-4 mt-1 shrink-0"
            >
              {showOverpaidDetail ? "Ukryj szczegóły" : "Pokaż faktury"}
            </button>
          )}
        </div>
        {(() => {
          if (!showOverpaidDetail || !overpaid) return null;
          const statusBadge = (status: string) => (
            <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold ${
              status === "unpaid"
                ? "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-400"
                : "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-400"
            }`}>
              {status === "unpaid" ? "Nieopłacona" : "Częściowo"}
            </span>
          );
          if (isMobile) {
            return (
              <Modal open title="Nadpłacony VAT (automatycznie)" onClose={() => setShowOverpaidDetail(false)} size="md">
                <div className="flex flex-col gap-3">
                  {overpaid.invoices.map((inv) => (
                    <div key={inv.invoice_id} className="rounded-lg border border-gray-200 dark:border-gray-700 p-3 flex flex-col gap-2">
                      <div className="flex items-start justify-between gap-2">
                        <span className="font-mono font-semibold text-sm text-gray-900 dark:text-gray-100">{inv.invoice_number}</span>
                        <span className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">{fmtDate(inv.invoice_date)}</span>
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">{inv.project_name}</div>
                      <div className="flex items-center justify-between gap-2 pt-1 border-t border-gray-100 dark:border-gray-800">
                        <div className="flex flex-col gap-0.5">
                          <span className="text-[10px] uppercase tracking-wide text-gray-400">Brutto</span>
                          <span className="font-mono text-sm text-gray-700 dark:text-gray-300">{fmt(inv.amount_gross)}</span>
                        </div>
                        <div className="flex flex-col gap-0.5 text-right">
                          <span className="text-[10px] uppercase tracking-wide text-gray-400">VAT zakredytowany</span>
                          <span className="font-mono text-sm font-bold text-gray-900 dark:text-gray-100">{fmt(inv.vat_credited)}</span>
                        </div>
                      </div>
                      <div>{statusBadge(inv.payment_status)}</div>
                    </div>
                  ))}
                </div>
              </Modal>
            );
          }
          return (
            <div className="mt-3 border-t border-gray-200 dark:border-gray-700 pt-3">
              <table className="w-full text-xs border-collapse">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-gray-700">
                    <th className="text-left pb-2 pr-3 font-mono text-[10px] uppercase tracking-widest text-gray-400 dark:text-gray-500">Nr faktury</th>
                    <th className="text-left pb-2 pr-3 font-mono text-[10px] uppercase tracking-widest text-gray-400 dark:text-gray-500">Data</th>
                    <th className="text-left pb-2 pr-3 font-mono text-[10px] uppercase tracking-widest text-gray-400 dark:text-gray-500">Przedsięwzięcie</th>
                    <th className="text-right pb-2 pr-3 font-mono text-[10px] uppercase tracking-widest text-gray-400 dark:text-gray-500">Brutto</th>
                    <th className="text-right pb-2 pr-3 font-mono text-[10px] uppercase tracking-widest text-gray-400 dark:text-gray-500">VAT zakredytowany</th>
                    <th className="text-left pb-2 font-mono text-[10px] uppercase tracking-widest text-gray-400 dark:text-gray-500">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {overpaid.invoices.map((inv) => (
                    <tr key={inv.invoice_id} className="border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors">
                      <td className="py-2 pr-3 font-mono font-medium text-gray-800 dark:text-gray-200">{inv.invoice_number}</td>
                      <td className="py-2 pr-3 text-gray-500 dark:text-gray-400 whitespace-nowrap">{fmtDate(inv.invoice_date)}</td>
                      <td className="py-2 pr-3 text-gray-600 dark:text-gray-300">{inv.project_name}</td>
                      <td className="py-2 pr-3 text-right font-mono text-gray-600 dark:text-gray-400">{fmt(inv.amount_gross)}</td>
                      <td className="py-2 pr-3 text-right font-mono font-semibold text-gray-900 dark:text-gray-100">{fmt(inv.vat_credited)}</td>
                      <td className="py-2">{statusBadge(inv.payment_status)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        })()}
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-3 gap-2 sm:gap-3">
        <div className="bg-white dark:bg-gray-800 rounded-lg p-2 sm:p-3 border border-gray-200 dark:border-gray-700 min-w-0">
          <div className="flex items-start justify-between gap-1">
            <p className="text-[9px] sm:text-xs text-gray-500 dark:text-gray-400 font-medium uppercase tracking-wide mb-1 leading-tight">
              VAT spłynięty
            </p>
            {((vatCollected?.by_company ?? []).some(
              (c) => c.invoices.length > 0,
            ) ||
              (vatCollected?.other_costs?.count ?? 0) > 0) && (
              <button
                onClick={() => setShowCollectedDetail((v) => !v)}
                className="text-[9px] sm:text-xs text-gray-400 hover:text-gray-600 underline shrink-0 leading-tight"
              >
                {showCollectedDetail ? "Ukryj" : "Faktury"}
              </button>
            )}
          </div>
          <p className="text-[11px] sm:text-lg font-bold text-gray-800 dark:text-gray-100 leading-snug break-words">
            {(() => {
              const t = parseFloat(vatCollected?.total ?? "0");
              if (t > 0) return `−${fmt(vatCollected!.total)}`;
              if (t < 0) return `+${fmt(String(-t))}`;
              return fmt("0");
            })()}
          </p>
          <p className="text-[9px] sm:text-xs text-gray-400 mt-0.5 leading-tight hidden sm:block">
            VAT z wpłat inwestorów minus VAT z FV kosztowych — saldo do rozliczenia z US
          </p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg p-2 sm:p-3 border border-gray-200 dark:border-gray-700 min-w-0">
          <p className="text-[9px] sm:text-xs text-gray-500 dark:text-gray-400 font-medium uppercase tracking-wide mb-1 leading-tight">
            VAT do zapłaty
          </p>
          <p className="text-[11px] sm:text-lg font-bold text-gray-800 dark:text-gray-100 leading-snug break-words">
            {sum("payable") > 0 ? `−${fmt(String(sum("payable")))}` : fmt("0")}
          </p>
          <p className="text-[9px] sm:text-xs text-gray-400 mt-0.5 leading-tight hidden sm:block">
            Planowane płatności do US (25. nast. m-ca)
          </p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg p-2 sm:p-3 border border-gray-200 dark:border-gray-700 min-w-0">
          <p className="text-[9px] sm:text-xs text-gray-500 dark:text-gray-400 font-medium uppercase tracking-wide mb-1 leading-tight">
            CIT / zaliczki
          </p>
          <p className="text-[11px] sm:text-lg font-bold text-gray-800 dark:text-gray-100 leading-snug break-words">
            {sum("cit") > 0 ? `−${fmt(String(sum("cit")))}` : fmt("0")}
          </p>
          <p className="text-[9px] sm:text-xs text-gray-400 mt-0.5 leading-tight hidden sm:block">
            Planowane do opłacenia (PIT i ZUS ujęte w kosztach płac)
          </p>
        </div>
      </div>

      {/* Full width — the breakdown carries nine columns and never fitted the card. */}
      {showCollectedDetail && vatCollected && (
        <VATCollectedDetail
          vatCollected={vatCollected}
          isMobile={isMobile}
          onClose={() => setShowCollectedDetail(false)}
        />
      )}

      <div className="flex justify-between items-center">
        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
          Zapisy VAT / CIT
        </h3>
        <button
          onClick={() => setShowForm(true)}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-[var(--color-primary)] text-white text-sm rounded-lg hover:bg-[var(--color-primary-hover)]"
        >
          <Plus size={14} /> Dodaj zapis
        </button>
      </div>

      {showForm && (
        <div className="bg-gray-50 dark:bg-gray-800/50 p-4 flex flex-col gap-3">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div>
              <label className="text-xs text-gray-500 block mb-1">
                Spółka GK ELEKTIM
              </label>
              <select
                value={form.company}
                onChange={(e) =>
                  setForm((f) => ({ ...f, company: e.target.value }))
                }
                className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-2 py-1.5 text-sm bg-white dark:bg-gray-700"
              >
                <option value="">— wszystkie / brak —</option>
                {companies.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.short_code}
                    {c.name ? ` — ${c.name}` : ""}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs text-gray-500 block mb-1">Typ</label>
              <select
                value={form.entry_type}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    entry_type: e.target.value as VATEntryType,
                    amount: "",
                  }))
                }
                className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-2 py-1.5 text-sm bg-white dark:bg-gray-700"
              >
                <option value="payable">
                  VAT do zapłaty (za poprzedni miesiąc)
                </option>
                <option value="cit">
                  CIT / zaliczka CIT (planowane do opłacenia)
                </option>
              </select>
            </div>
            <div>
              <label className="text-xs text-gray-500 block mb-1">
                {form.entry_type === "payable"
                  ? "Miesiąc, za który płacimy"
                  : "Miesiąc"}
              </label>
              <input
                type="month"
                value={form.period.slice(0, 7)}
                onChange={(e) =>
                  setForm((f) => ({ ...f, period: e.target.value + "-01" }))
                }
                className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-2 py-1.5 text-sm bg-white dark:bg-gray-700"
              />
            </div>
            <div>
              <label className="text-xs text-gray-500 block mb-1">
                Kwota (PLN)
              </label>
              <input
                type="number"
                step="0.01"
                value={form.amount}
                onChange={(e) =>
                  setForm((f) => ({ ...f, amount: e.target.value }))
                }
                className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-2 py-1.5 text-sm bg-white dark:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]"
              />
            </div>
            {form.entry_type !== "payable" && form.entry_type !== "cit" && (
              <div>
                <label className="text-xs text-gray-500 block mb-1">
                  Nr faktury (opcja)
                </label>
                <input
                  type="text"
                  value={form.invoice_ref}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, invoice_ref: e.target.value }))
                  }
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-2 py-1.5 text-sm bg-white dark:bg-gray-700"
                />
              </div>
            )}
            <div className="col-span-2 md:col-span-4">
              <label className="text-xs text-gray-500 block mb-1">Opis</label>
              <input
                type="text"
                value={form.description}
                onChange={(e) =>
                  setForm((f) => ({ ...f, description: e.target.value }))
                }
                className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-2 py-1.5 text-sm bg-white dark:bg-gray-700"
              />
            </div>
          </div>

          {/* Auto-suggestion for CIT */}
          {form.entry_type === "cit" && citSuggestion && (
            <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-700 rounded-lg px-4 py-3 text-sm flex flex-col gap-2">
              <p className="font-medium text-purple-800 dark:text-purple-300 text-xs uppercase tracking-wide">
                Przychód netto wystawiony w wybranym miesiącu
              </p>
              <div className="flex items-center gap-4">
                <span className="text-xs text-gray-500">
                  {fmtPeriod(citSuggestion.period)}:
                </span>
                <span className="text-gray-700 dark:text-gray-300 font-mono font-semibold">
                  {fmt(citSuggestion.net_revenue)} PLN
                </span>
                <span className="text-xs text-gray-400">
                  ({citSuggestion.invoice_count} FV)
                </span>
              </div>
            </div>
          )}

          {/* Auto-suggestion for payable VAT */}
          {form.entry_type === "payable" && suggestion && (
            <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-700 rounded-lg px-4 py-3 text-sm flex flex-col gap-2">
              <p className="font-medium text-orange-800 dark:text-orange-300 text-xs uppercase tracking-wide">
                VAT z faktur wystawionych w wybranym miesiącu
              </p>
              <div className="flex items-center gap-4">
                <span className="text-xs text-gray-500">
                  {fmtPeriod(form.period)}:
                </span>
                <span className="text-gray-700 dark:text-gray-300 font-mono font-semibold">
                  {fmt(suggestion.vat_from_invoices)} PLN
                </span>
                <span className="text-xs text-gray-400">
                  ({suggestion.invoice_count} FV)
                </span>
              </div>
              {suggestion.current_period &&
                parseFloat(suggestion.vat_current_month) > 0 && (
                  <div className="flex items-center gap-4">
                    <span className="text-xs text-gray-500">
                      {fmtPeriod(suggestion.current_period)} (bieżący):
                    </span>
                    <span className="text-gray-700 dark:text-gray-300 font-mono font-semibold">
                      {fmt(suggestion.vat_current_month)} PLN
                    </span>
                    <span className="text-xs text-gray-400">
                      ({suggestion.current_invoice_count} FV)
                    </span>
                  </div>
                )}
              {parseFloat(suggestion.vat_from_invoices) > 0 && (
                <div className="flex gap-2 mt-1">
                  <button
                    type="button"
                    onClick={() =>
                      setForm((f) => ({
                        ...f,
                        amount: parseFloat(
                          suggestion.vat_from_invoices,
                        ).toFixed(2),
                      }))
                    }
                    className="text-xs text-orange-700 dark:text-orange-300 underline hover:no-underline"
                  >
                    Wstaw kwotę z {fmtPeriod(form.period)}
                  </button>
                  {suggestion.current_period &&
                    parseFloat(suggestion.vat_current_month) > 0 && (
                      <button
                        type="button"
                        onClick={() =>
                          setForm((f) => ({
                            ...f,
                            amount: (
                              parseFloat(suggestion.vat_from_invoices) +
                              parseFloat(suggestion.vat_current_month)
                            ).toFixed(2),
                          }))
                        }
                        className="text-xs text-orange-700 dark:text-orange-300 underline hover:no-underline"
                      >
                        Wstaw sumę obu miesięcy
                      </button>
                    )}
                </div>
              )}
            </div>
          )}

          <div className="flex gap-2">
            <button
              onClick={save}
              disabled={isPending || !form.amount}
              className="px-4 py-1.5 bg-[var(--color-primary)] text-white text-sm rounded-lg hover:bg-[var(--color-primary-hover)] disabled:opacity-50"
            >
              {isPending ? "Zapisywanie..." : "Zapisz"}
            </button>
            <button
              onClick={() => setShowForm(false)}
              className="px-4 py-1.5 text-sm text-gray-600 dark:text-gray-400 "
            >
              Anuluj
            </button>
          </div>
        </div>
      )}

      <div className="bg-white dark:bg-gray-800 overflow-hidden">
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-gray-50 dark:bg-gray-800/60 text-xs uppercase tracking-wide text-gray-400 border-b border-gray-200 dark:border-gray-700">
              <th className="text-left px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">
                Typ
              </th>
              <th className="text-left px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">
                Spółka
              </th>
              <th className="text-right px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">
                Kwota
              </th>
              <th className="text-left px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">
                Miesiąc
              </th>
              <th className="text-left px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">
                Nr faktury
              </th>
              <th className="text-left px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">
                Opis
              </th>
              <th className="text-left px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">
                Dodał
              </th>
              <th className="px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400"></th>
            </tr>
          </thead>
          <tbody>
            {entries.length === 0 ? (
              <tr>
                <td
                  colSpan={8}
                  className="px-4 py-6 text-center text-gray-400 text-sm"
                >
                  Brak zapisów.
                </td>
              </tr>
            ) : (
              entries.map((e) => (
                <tr
                  key={e.id}
                  className={`border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/30 ${e.is_paid ? "opacity-50" : ""}`}
                >
                  <td className="px-4 py-2">
                    <span
                      className={`text-xs font-semibold px-2 py-0.5 rounded-full ${VAT_TYPE_COLORS[e.entry_type as VATEntryType] ?? "bg-gray-100 text-gray-700"}`}
                    >
                      {VAT_TYPE_LABELS[e.entry_type as VATEntryType] ??
                        e.entry_type}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-xs text-gray-500">
                    {e.company_name ?? <span className="text-gray-300">—</span>}
                  </td>
                  <td className="px-4 py-2 text-right font-mono">
                    {e.entry_type === "carryforward" ? (
                      <span className="text-red-700 dark:text-red-300">
                        −{fmt(e.amount)}
                      </span>
                    ) : (
                      <span className="text-gray-800 dark:text-gray-200">
                        {fmt(e.amount)}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-gray-500">
                    {fmtPeriod(e.period)}
                  </td>
                  <td className="px-4 py-2 text-gray-400 text-xs">
                    {e.invoice_ref || "—"}
                  </td>
                  <td className="px-4 py-2 text-gray-400 text-xs">
                    {e.description || "—"}
                  </td>
                  <td className="px-4 py-2 text-gray-400 text-xs">
                    {e.created_by_name ?? "—"}
                  </td>
                  <td className="px-4 py-2">
                    {e.is_paid ? (
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() =>
                            markPaid({
                              id: e.id,
                              is_paid: false,
                              paid_date: null,
                            })
                          }
                          title={`Opłacono ${formatDate(e.paid_date, "")}. Kliknij, aby cofnąć.`}
                          className="text-xs px-2 py-0.5 rounded border border-green-400 text-green-700 bg-green-50 hover:bg-green-100 transition-colors"
                        >
                          ✓ Opłacone {formatDate(e.paid_date, "")}
                        </button>
                        <button
                          onClick={() => del(e.id)}
                          className="text-red-400 hover:text-red-600"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    ) : pendingPaidId === e.id ? (
                      <div className="flex items-center gap-1 flex-wrap">
                        <DatePicker
                          value={pendingPaidDate}
                          onChange={setPendingPaidDate}
                        />
                        <button
                          onClick={() => {
                            markPaid({
                              id: e.id,
                              is_paid: true,
                              paid_date: pendingPaidDate,
                            });
                            setPendingPaidId(null);
                            setPendingPaidDate(today);
                          }}
                          className="text-xs px-2 py-0.5 rounded border border-green-500 text-green-700 bg-green-50 hover:bg-green-100"
                        >
                          Potwierdź
                        </button>
                        <button
                          onClick={() => {
                            setPendingPaidId(null);
                            setPendingPaidDate(today);
                          }}
                          className="text-xs px-1.5 py-0.5 rounded border border-gray-300 text-gray-500 hover:bg-gray-100"
                        >
                          ✕
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => {
                            setPendingPaidId(e.id);
                            setPendingPaidDate(today);
                          }}
                          className="text-xs px-2 py-0.5 rounded border border-gray-300 text-gray-500 hover:border-green-400 hover:text-green-700 transition-colors"
                        >
                          Opłać
                        </button>
                        <button
                          onClick={() => del(e.id)}
                          className="text-red-400 hover:text-red-600"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Balance history chart ──────────────────────────────────────────────────

const BH_COLORS = [
  "#2563eb",
  "#16a34a",
  "#dc2626",
  "#d97706",
  "#7c3aed",
  "#0891b2",
];
const DAY_MS_BH = 86_400_000;

function tsToLabelBH(ts: number): string {
  return new Date(ts).toLocaleDateString("pl", {
    day: "2-digit",
    month: "short",
    year: "2-digit",
  });
}

const GK_BALANCE_COMPANIES_KEY = "gk_balance_companies";
const GK_BALANCE_PRESET_KEY = "gk_balance_preset";
const BH_PRESETS = ["3m", "6m", "1y", "all"] as const;
type BHPreset = (typeof BH_PRESETS)[number];

// Opaque, elevated tooltip so it fully covers the chart lines / legend underneath.
function BalanceTooltip({
  active,
  payload,
  label,
}: {
  active?: boolean;
  payload?: Array<{
    name?: string;
    value?: number;
    color?: string;
    dataKey?: string | number;
  }>;
  label?: number;
}) {
  if (!active || !payload || payload.length === 0) return null;

  const dateStr =
    typeof label === "number"
      ? formatDate(label)
      : "";

  const fmt = (v?: number) =>
    `${formatAmount(String((v ?? 0).toFixed(2)))} PLN`;
  const rows = payload.filter((p) => p.dataKey !== "Suma");
  const suma = payload.find((p) => p.dataKey === "Suma");

  return (
    <div className="min-w-[210px] rounded-xl border border-gray-200 bg-white p-3 text-sm shadow-2xl ring-1 ring-black/5 dark:border-gray-600 dark:bg-gray-800 dark:ring-white/10">
      {dateStr && (
        <div className="mb-2 border-b border-gray-100 pb-1.5 font-semibold text-gray-700 dark:border-gray-700 dark:text-gray-200">
          {dateStr}
        </div>
      )}
      <div className="space-y-1">
        {rows.map((p) => (
          <div
            key={String(p.dataKey)}
            className="flex items-center justify-between gap-6"
          >
            <span className="flex items-center gap-1.5 text-gray-600 dark:text-gray-300">
              <span
                className="inline-block h-2.5 w-2.5 shrink-0 rounded-full"
                style={{ backgroundColor: p.color }}
              />
              {p.name}
            </span>
            <span className="font-medium tabular-nums text-gray-800 dark:text-gray-100">
              {fmt(p.value)}
            </span>
          </div>
        ))}
      </div>
      {suma && (
        <div className="mt-2 flex items-center justify-between gap-6 border-t border-gray-200 pt-1.5 dark:border-gray-700">
          <span className="font-semibold text-gray-700 dark:text-gray-200">
            Suma
          </span>
          <span className="font-bold tabular-nums text-gray-900 dark:text-white">
            {fmt(suma.value)}
          </span>
        </div>
      )}
    </div>
  );
}

function BalanceHistoryChart({
  checkpoints,
}: {
  checkpoints: import("@/features/group_finance/api/groupFinance").BalanceCheckpoint[];
}) {
  const todayTs = useMemo(() => Date.now(), []);
  const [activePreset, setActivePresetRaw] = useState<BHPreset>(() => {
    const stored = localStorage.getItem(GK_BALANCE_PRESET_KEY);
    return (BH_PRESETS as readonly string[]).includes(stored ?? "")
      ? (stored as BHPreset)
      : "all";
  });
  const setActivePreset = (p: BHPreset) => {
    setActivePresetRaw(p);
    localStorage.setItem(GK_BALANCE_PRESET_KEY, p);
  };
  const [zoomDomain, setZoomDomain] = useState<[number, number] | null>(null);

  // All companies present in history data
  const availableCompanies = useMemo(() => {
    const map = new Map<string, string>(); // id → code
    checkpoints.forEach((c) => map.set(c.company, c.company_code));
    return [...map.entries()]
      .map(([id, code]) => ({ id, code }))
      .sort((a, b) => a.code.localeCompare(b.code));
  }, [checkpoints]);

  const allIds = useMemo(
    () => new Set(availableCompanies.map((c) => c.id)),
    [availableCompanies],
  );
  const [selectedIds, setSelectedIdsRaw] = useState<Set<string> | null>(() =>
    readStoredIds(GK_BALANCE_COMPANIES_KEY),
  );
  const setSelectedIds = (ids: Set<string> | null) => {
    setSelectedIdsRaw(ids);
    writeStoredIds(GK_BALANCE_COMPANIES_KEY, ids);
  };
  const effectiveIds = selectedIds ?? allIds;

  const toggleCompany = (id: string) => {
    const next = new Set(effectiveIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelectedIds(next);
  };

  const filtered = useMemo(
    () => checkpoints.filter((c) => effectiveIds.has(c.company)),
    [checkpoints, effectiveIds],
  );

  const companies = useMemo(
    () => [...new Set(filtered.map((c) => c.company_code))].sort(),
    [filtered],
  );

  const chartData = useMemo(() => {
    if (filtered.length === 0) return [];

    const sorted = [...filtered].sort((a, b) =>
      a.recorded_at.localeCompare(b.recorded_at),
    );
    const uniqueIsos = [...new Set(sorted.map((c) => c.recorded_at))].sort();

    const todayIso = new Date(todayTs).toISOString();
    if (uniqueIsos[uniqueIsos.length - 1] < todayIso) uniqueIsos.push(todayIso);

    const lastKnown: Record<string, number> = {};

    return uniqueIsos.map((isoTs) => {
      sorted
        .filter((c) => c.recorded_at === isoTs)
        .forEach((c) => {
          lastKnown[c.company_code] =
            parseFloat(c.net_amount) + parseFloat(c.vat_amount);
        });

      const point: Record<string, number> = { ts: new Date(isoTs).getTime() };
      companies.forEach((code) => {
        point[code] = lastKnown[code] ?? 0;
      });
      point["Suma"] = companies.reduce(
        (s, code) => s + (lastKnown[code] ?? 0),
        0,
      );
      return point;
    });
  }, [filtered, companies, todayTs]);

  const applyPreset = (preset: "3m" | "6m" | "1y" | "all") => {
    setActivePreset(preset);
    if (preset === "all") {
      setZoomDomain(null);
    } else {
      const months = preset === "3m" ? 3 : preset === "6m" ? 6 : 12;
      setZoomDomain([
        todayTs - DAY_MS_BH * 30 * months,
        todayTs + DAY_MS_BH * 7,
      ]);
    }
  };

  // Carry-forward: find last known point before window, use as start anchor
  const visibleData = useMemo(() => {
    if (!zoomDomain || chartData.length === 0) return chartData;
    const [lo, hi] = zoomDomain;

    let anchor: Record<string, number> | null = null;
    for (const p of chartData) {
      if (p.ts <= lo) anchor = p;
      else break;
    }

    const inRange = chartData.filter((p) => p.ts > lo && p.ts <= hi);
    const startPoint = anchor
      ? { ...anchor, ts: lo }
      : {
          ts: lo,
          Suma: 0,
          ...Object.fromEntries(companies.map((c) => [c, 0])),
        };

    if (inRange.length === 0) {
      return [startPoint, { ...startPoint, ts: hi }];
    }
    return [startPoint, ...inRange, { ...inRange[inRange.length - 1], ts: hi }];
  }, [chartData, zoomDomain, companies]);

  if (availableCompanies.length === 0) return null;

  const formatY = (v: number) => {
    const abs = Math.abs(v);
    if (abs >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
    if (abs >= 1_000) return `${(v / 1_000).toFixed(0)}k`;
    return v.toString();
  };

  const btnBase = "px-2.5 py-1 text-xs rounded font-medium transition-colors";
  const btnActive = "bg-[var(--color-primary)] text-white";
  const btnInactive =
    "bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200";

  return (
    <div className="bg-white dark:bg-gray-800 p-4">
      {/* Company selector */}
      <div className="flex flex-wrap items-center gap-x-5 gap-y-2 mb-3 pb-3 border-b border-gray-100 dark:border-gray-700">
        <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
          Spółki:
        </span>
        {availableCompanies.map((co) => (
          <label
            key={co.id}
            className="flex items-center gap-1.5 text-sm cursor-pointer select-none"
          >
            <input
              type="checkbox"
              className="rounded accent-[var(--color-primary)]"
              checked={effectiveIds.has(co.id)}
              onChange={() => toggleCompany(co.id)}
            />
            <span className="font-medium text-gray-700 dark:text-gray-300">
              {co.code}
            </span>
          </label>
        ))}
      </div>

      {/* Title + range presets */}
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
          Historia sald kont
        </h3>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs text-gray-400 dark:text-gray-500 mr-1">
            Zakres:
          </span>
          {(["3m", "6m", "1y", "all"] as const).map((p) => (
            <button
              key={p}
              className={`${btnBase} ${activePreset === p ? btnActive : btnInactive}`}
              onClick={() => applyPreset(p)}
            >
              {p === "3m"
                ? "3 mies."
                : p === "6m"
                  ? "6 mies."
                  : p === "1y"
                    ? "1 rok"
                    : "Wszystko"}
            </button>
          ))}
        </div>
      </div>

      <div className="h-[180px] sm:h-[300px]">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            data={visibleData}
            margin={{ top: 8, right: 8, left: 4, bottom: 5 }}
          >
            <CartesianGrid
              strokeDasharray="3 3"
              stroke="#f0f0f0"
              vertical={false}
            />
            <XAxis
              dataKey="ts"
              type="number"
              scale="time"
              domain={["dataMin", "dataMax"]}
              tickFormatter={tsToLabelBH}
              tick={{ fontSize: 11 }}
            />
            <YAxis tickFormatter={formatY} tick={{ fontSize: 11 }} width={55} />
            <Tooltip
              content={<BalanceTooltip />}
              isAnimationActive={false}
              cursor={{ stroke: "#9ca3af", strokeDasharray: "3 3" }}
              wrapperStyle={{ zIndex: 50, outline: "none" }}
            />
            <Legend />
            {companies.map((code, i) => (
              <Line
                key={code}
                type="stepAfter"
                dataKey={code}
                stroke={BH_COLORS[i % BH_COLORS.length]}
                strokeWidth={2}
                dot={{ r: 3 }}
              />
            ))}
            {companies.length > 1 && (
              <Line
                type="stepAfter"
                dataKey="Suma"
                stroke="#111827"
                strokeWidth={2.5}
                strokeDasharray="5 3"
                dot={false}
              />
            )}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

// ── Helpers ────────────────────────────────────────────────────────────────

function fmtPLN(value: number): string {
  return value.toLocaleString("pl-PL", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    useGrouping: "always",
  });
}

function monthLabel(ym: string): string {
  const [y, m] = ym.split("-");
  return new Date(parseInt(y), parseInt(m) - 1, 1).toLocaleDateString("pl-PL", {
    month: "long",
    year: "numeric",
  });
}

// ── Monthly summary table ──────────────────────────────────────────────────

interface MonthRow {
  month: string;
  revenue: number;
  cost: number;
  result: number;
  issuedHint: number;
  draftsHint: number;
  isFuturePlanned: boolean;
  // Partner (JV) invoice share netted OUT of revenue this month (negative). It is
  // NOT a cost — in a consortium we bill the client the full amount and pay the
  // partner their share, so our real revenue is net of that share.
  jvReduction: number;
}

export function MonthlySummaryTable({
  events,
  companies,
}: {
  events: CashflowEvent[];
  companies?: string[];
}) {
  const currentMonth = useMemo(() => new Date().toISOString().slice(0, 7), []);

  const { data: extraCosts = {} } = useMonthlyExtraCosts();
  const { mutate: setExtraCost } = useSetMonthlyExtraCost();
  // Revenue per month comes from the backend (net of PRZ invoices and
  // proformas, by document issue month), not from summing positive cashflow
  // events on the frontend.
  const { data: przRevenue } = useMonthlyPrzRevenue(companies);

  const [editingMonth, setEditingMonth] = useState<string | null>(null);
  const [draftValue, setDraftValue] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  const startEdit = useCallback((month: string) => {
    const current = extraCosts[month] ?? "0";
    setDraftValue(parseFloat(current) === 0 ? "" : String(parseFloat(current)));
    setEditingMonth(month);
  }, [extraCosts]);

  useEffect(() => {
    if (editingMonth && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editingMonth]);

  const commitEdit = useCallback(() => {
    if (!editingMonth) return;
    const parsed = parseFloat(draftValue.replace(",", "."));
    const amount = isNaN(parsed) || parsed < 0 ? "0" : String(parsed);
    setExtraCost({ yearMonth: editingMonth, amount });
    setEditingMonth(null);
  }, [editingMonth, draftValue, setExtraCost]);

  const cancelEdit = useCallback(() => setEditingMonth(null), []);

  const rows = useMemo<MonthRow[]>(() => {
    type Acc = { revenue: number; cost: number; plannedRevenue: number; jv: number };
    const byMonth: Record<string, Acc> = {};
    const ensure = (month: string) => {
      if (!byMonth[month]) byMonth[month] = { revenue: 0, cost: 0, plannedRevenue: 0, jv: 0 };
      return byMonth[month];
    };
    for (const ev of events) {
      const month = ev.date.slice(0, 7);
      const acc = ensure(month);
      const amount = parseFloat(ev.amount);
      // JV partner invoice is NOT a cost — it nets out of revenue (our share).
      if (ev.type === "jv_partner_invoice") {
        acc.jv += amount; // negative
        continue;
      }
      if (amount < 0) acc.cost += amount;
      if (ev.type === "planned_revenue" && amount > 0) acc.plannedRevenue += amount;
    }
    // Revenue from issued PRZ invoices (past + current months).
    for (const [month, net] of Object.entries(przRevenue?.issued ?? {})) {
      ensure(month).revenue = parseFloat(net);
    }
    // Current month: match the PRZ invoices page summary — issued + ALL
    // drafts (including unapproved), by document issue month.
    const currentDrafts = parseFloat(przRevenue?.drafts?.[currentMonth] ?? "0");
    if (currentDrafts > 0) ensure(currentMonth);
    const sorted = Object.entries(byMonth)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([month, d]) => {
        const isFuture = month > currentMonth;
        const isCurr = month === currentMonth;
        // Future months: use planned_revenue events instead of issued invoices.
        const grossRevenue = isFuture
          ? d.plannedRevenue
          : isCurr
            ? d.revenue + currentDrafts
            : d.revenue;
        // Revenue is ALWAYS shown net of the JV partner share (d.jv is negative).
        const effectiveRevenue = grossRevenue + d.jv;
        return {
          month,
          revenue: effectiveRevenue,
          cost: d.cost,
          result: effectiveRevenue + d.cost,
          issuedHint: isCurr && currentDrafts > 0 ? d.revenue : 0,
          draftsHint: isCurr ? currentDrafts : 0,
          isFuturePlanned: isFuture,
          jvReduction: d.jv,
        };
      });

    // Show all months that have any revenue or costs.
    const lastIdx = sorted.reduce(
      (last, r, i) => (r.revenue > 0 || r.cost < 0 ? i : last),
      -1,
    );
    return lastIdx >= 0 ? sorted.slice(0, lastIdx + 1) : sorted;
  }, [events, przRevenue, currentMonth]);

  const totals = useMemo(
    () =>
      rows.reduce(
        (acc, r) => {
          const extra = parseFloat(extraCosts[r.month] ?? "0");
          return {
            revenue: acc.revenue + r.revenue,
            cost: acc.cost + r.cost,
            extra: acc.extra + extra,
            indicative: acc.indicative + r.result - extra,
          };
        },
        { revenue: 0, cost: 0, extra: 0, indicative: 0 },
      ),
    [rows, extraCosts],
  );

  if (rows.length === 0) return null;

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
      <div className="px-5 py-3 border-b border-gray-200 dark:border-gray-700">
        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-200 tracking-wide uppercase">
          Zestawienie miesięczne
        </h3>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-750">
              <th className="text-left px-5 py-2.5 font-medium text-gray-500 dark:text-gray-400 w-40">
                Miesiąc
              </th>
              <th className="text-right px-5 py-2.5 font-medium text-gray-500 dark:text-gray-400">
                Przychód
              </th>
              <th className="text-right px-5 py-2.5 font-medium text-gray-500 dark:text-gray-400">
                Koszt
              </th>
              <th className="text-right px-5 py-2.5 font-medium text-gray-500 dark:text-gray-400 border-l border-gray-200 dark:border-gray-700">
                Dod. koszt plan.
              </th>
              <th className="text-right px-5 py-2.5 font-medium text-gray-500 dark:text-gray-400 border-l border-gray-200 dark:border-gray-700">
                Poglądowy wynik
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-700/60">
            {rows.map((row) => {
              const isPast = row.month < currentMonth;
              const isCurrent = row.month === currentMonth;
              const extra = parseFloat(extraCosts[row.month] ?? "0");
              const indicative = row.result - extra;
              const isEditing = editingMonth === row.month;

              return (
                <tr
                  key={row.month}
                  className={[
                    "transition-colors",
                    isCurrent
                      ? "bg-blue-50/70 dark:bg-blue-900/20"
                      : isPast
                        ? "opacity-60 hover:opacity-100"
                        : "hover:bg-gray-50 dark:hover:bg-gray-700/30",
                  ].join(" ")}
                >
                  <td className="px-5 py-2.5 font-medium text-gray-700 dark:text-gray-300 capitalize flex items-center gap-2">
                    {isCurrent && (
                      <span className="inline-block w-1.5 h-1.5 rounded-full bg-blue-500 shrink-0" />
                    )}
                    {monthLabel(row.month)}
                  </td>
                  <td className="px-5 py-2.5 text-right tabular-nums text-emerald-600 dark:text-emerald-400">
                    {row.revenue > 0 ? `+${fmtPLN(row.revenue)}` : "—"}
                    {isCurrent && row.draftsHint > 0 && (
                      <span className="block text-xs text-gray-400 dark:text-gray-500 font-normal tabular-nums">
                        ({fmtPLN(row.issuedHint)} wystawione + {fmtPLN(row.draftsHint)} szkice)
                      </span>
                    )}
                    {row.jvReduction < 0 && (
                      <span
                        className="block text-xs text-gray-400 dark:text-gray-500 font-normal tabular-nums"
                        title="Przychód pomniejszony o udział partnera konsorcjum (FV partnera JV) — to nie jest koszt"
                      >
                        (netto — po udziale partnera JV {fmtPLN(row.jvReduction)})
                      </span>
                    )}
                  </td>
                  <td className="px-5 py-2.5 text-right tabular-nums text-red-500 dark:text-red-400">
                    {row.cost < 0 ? fmtPLN(row.cost) : "—"}
                  </td>
                  <td
                    className="px-5 py-2 text-right tabular-nums border-l border-gray-200 dark:border-gray-700 cursor-pointer"
                    onClick={() => !isEditing && startEdit(row.month)}
                    title="Kliknij, aby edytować"
                  >
                    {isEditing ? (
                      <input
                        ref={inputRef}
                        type="number"
                        min="0"
                        step="0.01"
                        value={draftValue}
                        onChange={(e) => setDraftValue(e.target.value)}
                        onBlur={commitEdit}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") commitEdit();
                          if (e.key === "Escape") cancelEdit();
                        }}
                        className="w-28 text-right bg-white dark:bg-gray-700 border border-blue-400 rounded px-1.5 py-0.5 text-xs tabular-nums outline-none"
                        placeholder="0"
                      />
                    ) : (
                      <span className={extra > 0 ? "text-red-500 dark:text-red-400" : "text-gray-400 dark:text-gray-500"}>
                        {extra > 0 ? fmtPLN(-extra) : "—"}
                      </span>
                    )}
                  </td>
                  <td
                    className={[
                      "px-5 py-2.5 text-right tabular-nums font-semibold border-l border-gray-200 dark:border-gray-700",
                      indicative >= 0
                        ? "text-emerald-600 dark:text-emerald-400"
                        : "text-red-600 dark:text-red-400",
                    ].join(" ")}
                  >
                    {indicative >= 0 ? "+" : ""}
                    {fmtPLN(indicative)}
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr className="border-t-2 border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-750 font-semibold">
              <td className="px-5 py-3 text-gray-700 dark:text-gray-200 text-xs uppercase tracking-wide">
                Łącznie
              </td>
              <td className="px-5 py-3 text-right tabular-nums text-emerald-600 dark:text-emerald-400">
                {totals.revenue > 0 ? `+${fmtPLN(totals.revenue)}` : "—"}
              </td>
              <td className="px-5 py-3 text-right tabular-nums text-red-500 dark:text-red-400">
                {totals.cost < 0 ? fmtPLN(totals.cost) : "—"}
              </td>
              <td className="px-5 py-3 text-right tabular-nums text-red-500 dark:text-red-400 border-l border-gray-200 dark:border-gray-700">
                {totals.extra > 0 ? fmtPLN(-totals.extra) : "—"}
              </td>
              <td
                className={[
                  "px-5 py-3 text-right tabular-nums border-l border-gray-200 dark:border-gray-700",
                  totals.indicative >= 0
                    ? "text-emerald-600 dark:text-emerald-400"
                    : "text-red-600 dark:text-red-400",
                ].join(" ")}
              >
                {totals.indicative >= 0 ? "+" : ""}
                {fmtPLN(totals.indicative)}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
      <p className="px-5 py-2 text-xs text-gray-400 dark:text-gray-500 border-t border-gray-100 dark:border-gray-700/60">
        Przychód pokazany netto — po odjęciu udziału partnerów konsorcjum (FV partnera JV).
        Udział partnera nie jest liczony jako koszt.
      </p>
    </div>
  );
}

// ── Tab: Wykres cashflow GK ────────────────────────────────────────────────

const GK_CASHFLOW_COMPANIES_KEY = "gk_cashflow_companies";

function readStoredIds(key: string): Set<string> | null {
  try {
    const raw = localStorage.getItem(key);
    if (raw === null || raw === "null") return null;
    return new Set(JSON.parse(raw) as string[]);
  } catch {
    return null;
  }
}

function writeStoredIds(key: string, ids: Set<string> | null) {
  localStorage.setItem(key, ids === null ? "null" : JSON.stringify([...ids]));
}

function WykresTab() {
  const { user } = useAuthStore();
  const { data: latest = [], isLoading: balancesLoading } = useLatestBalances();

  // Selected company IDs — null means "all" (default), persisted in localStorage
  const [selectedIds, setSelectedIdsRaw] = useState<Set<string> | null>(() =>
    readStoredIds(GK_CASHFLOW_COMPANIES_KEY),
  );
  const setSelectedIds = (ids: Set<string> | null) => {
    setSelectedIdsRaw(ids);
    writeStoredIds(GK_CASHFLOW_COMPANIES_KEY, ids);
  };

  const allIds = useMemo(
    () => new Set(latest.map((b) => b.company_id)),
    [latest],
  );
  // Treat empty Set same as null — empty selection = show all companies
  const effectiveIds =
    !selectedIds || selectedIds.size === 0 ? allIds : selectedIds;

  const selectedCompanies = useMemo(() => [...effectiveIds], [effectiveIds]);

  // Pass selectedIds===null as undefined (all companies), explicit empty Set as []
  const cashflowCompanies =
    selectedIds === null ? undefined : selectedCompanies;
  const {
    data: events = [],
    isLoading: eventsLoading,
    refetch: refetchCashflow,
  } = useGroupCashflow(cashflowCompanies);

  const activeBalances = useMemo(
    () => latest.filter((b) => effectiveIds.has(b.company_id)),
    [latest, effectiveIds],
  );

  const initialBalance = useMemo(
    () =>
      activeBalances.reduce((s, b) => s + parseFloat(b.net_balance ?? "0"), 0),
    [activeBalances],
  );

  const toggleCompany = (id: string) => {
    const current = effectiveIds;
    const next = new Set(current);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelectedIds(next);
  };

  const allSelected = selectedIds === null || effectiveIds.size === allIds.size;

  const toggleAll = () => {
    if (allSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(null);
    }
  };

  if (balancesLoading || eventsLoading) {
    return (
      <div className="flex items-center justify-center py-16 text-gray-400 text-sm">
        Ładowanie danych...
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {/* Company filter */}
      {latest.length > 0 && (
        <div className="bg-white dark:bg-gray-800 px-4 py-3">
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
            <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide shrink-0">
              Spółki:
            </span>
            {latest.map((b) => (
              <label
                key={b.company_id}
                className="flex items-center gap-1.5 text-sm cursor-pointer select-none"
              >
                <input
                  type="checkbox"
                  className="rounded accent-[var(--color-primary)]"
                  checked={effectiveIds.has(b.company_id)}
                  onChange={() => toggleCompany(b.company_id)}
                />
                <span className="font-medium text-gray-700 dark:text-gray-300">
                  {b.company_code}
                </span>
                {b.net_balance !== null && (
                  <span className="hidden sm:inline text-xs text-gray-400 dark:text-gray-500">
                    (
                    {parseFloat(b.net_balance).toLocaleString("pl-PL", {
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 0,
                      useGrouping: "always",
                    })}{" "}
                    PLN)
                  </span>
                )}
              </label>
            ))}
            <button
              onClick={toggleAll}
              className="text-xs text-[var(--color-primary)] hover:underline font-medium shrink-0"
            >
              {allSelected ? "Odznacz wszystkie" : "Zaznacz wszystkie"}
            </button>
          </div>
        </div>
      )}

      {/* Balance info strip */}
      <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
        <span>
          Saldo kont dziś ({activeBalances.length}{" "}
          {activeBalances.length === 1
            ? "spółka"
            : activeBalances.length < 5
              ? "spółki"
              : "spółek"}
          ):
        </span>
        <span
          className={`font-semibold ${initialBalance >= 0 ? "text-[var(--color-primary)] dark:text-blue-400" : "text-red-600"}`}
        >
          {initialBalance.toLocaleString("pl-PL", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
            useGrouping: "always",
          })}{" "}
          PLN
        </span>
        <span className="text-gray-400 dark:text-gray-500">
          — punkt startowy wykresu
        </span>
      </div>

      <p className="text-xs text-gray-400 dark:text-gray-500">
        Wykres uwzględnia faktury PRZ (wystawione), planowane przychody z
        przedsięwzięć, plany GK (przychody i koszty) oraz faktury P-GK
        (zaakceptowane).
      </p>
      <CashflowChart
        events={events}
        initialBalance={initialBalance}
        defaultPreset="1y"
        storageKey="gk_cashflow"
        onRefetch={refetchCashflow}
        monthMode={!user?.is_ceo}
      />

      <MonthlySummaryTable events={events} companies={cashflowCompanies} />
    </div>
  );
}

// ── Tab: Refaktury wewnętrzne GK ───────────────────────────────────────────

const emptyRecurringForm = {
  payer_company: "",
  recipient_company: "",
  description: "",
  amount_monthly: "",
  day_of_month: "1",
  start_date: new Date().toISOString().slice(0, 10),
  end_date: "",
};

const emptyOneTimeForm = {
  payer_company: "",
  recipient_company: "",
  description: "",
  amount: "",
  cost_date: new Date().toISOString().slice(0, 10),
};

function RefakturyTab() {
  const { data: companies = [] } = useCompanies();
  const { data: recurring = [] } = useIntercompanyRecurring();
  const { data: oneTime = [] } = useIntercompanyOneTime();
  const { mutate: createRec, isPending: creatingRec } =
    useCreateIntercompanyRecurring();
  const { mutate: updateRec } = useUpdateIntercompanyRecurring();
  const { mutate: deleteRec } = useDeleteIntercompanyRecurring();
  const { mutate: createOt, isPending: creatingOt } =
    useCreateIntercompanyOneTime();
  const { mutate: deleteOt } = useDeleteIntercompanyOneTime();

  const [showRecForm, setShowRecForm] = useState(false);
  const [recForm, setRecForm] = useState(emptyRecurringForm);
  const [editingRec, setEditingRec] = useState<IntercompanyRecurring | null>(
    null,
  );

  const [showOtForm, setShowOtForm] = useState(false);
  const [otForm, setOtForm] = useState(emptyOneTimeForm);

  function saveRec() {
    const payload = {
      payer_company: recForm.payer_company,
      recipient_company: recForm.recipient_company,
      description: recForm.description,
      amount_monthly: recForm.amount_monthly,
      day_of_month: parseInt(recForm.day_of_month),
      start_date: recForm.start_date,
      end_date: recForm.end_date || null,
    };
    if (editingRec) {
      updateRec(
        { id: editingRec.id, data: payload },
        {
          onSuccess: () => {
            setEditingRec(null);
            setRecForm(emptyRecurringForm);
          },
        },
      );
    } else {
      createRec(payload, {
        onSuccess: () => {
          setShowRecForm(false);
          setRecForm(emptyRecurringForm);
        },
      });
    }
  }

  function startEditRec(r: IntercompanyRecurring) {
    setEditingRec(r);
    setRecForm({
      payer_company: r.payer_company,
      recipient_company: r.recipient_company,
      description: r.description,
      amount_monthly: r.amount_monthly,
      day_of_month: String(r.day_of_month),
      start_date: r.start_date,
      end_date: r.end_date ?? "",
    });
    setShowRecForm(true);
  }

  function toggleSuspend(r: IntercompanyRecurring) {
    const today = new Date().toISOString().slice(0, 10);
    updateRec({
      id: r.id,
      data: { suspended_from: r.suspended_from ? null : today },
    });
  }

  function saveOt() {
    createOt(otForm, {
      onSuccess: () => {
        setShowOtForm(false);
        setOtForm(emptyOneTimeForm);
      },
    });
  }

  const inputCls =
    "w-full px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]";
  const labelCls =
    "block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1";

  return (
    <div className="flex flex-col gap-8">
      {/* ── Refaktury stałe ────────────────────────────────────────────── */}
      <div className="bg-white dark:bg-gray-800 ">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-gray-700">
          <div>
            <h3 className="font-semibold text-gray-900 dark:text-gray-100">
              Refaktury stałe
            </h3>
            <p className="text-xs text-gray-400 mt-0.5">
              Miesięczne przeniesienia kosztów między spółkami GK
            </p>
          </div>
          <button
            onClick={() => {
              setEditingRec(null);
              setRecForm(emptyRecurringForm);
              setShowRecForm((v) => !v);
            }}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-[var(--color-primary)] text-white rounded-lg text-sm hover:bg-[var(--color-primary-hover)]"
          >
            <Plus size={14} /> Dodaj
          </button>
        </div>

        {showRecForm && (
          <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/30">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-3">
              <div>
                <label className={labelCls}>Spółka płacąca *</label>
                <select
                  className={inputCls}
                  value={recForm.payer_company}
                  onChange={(e) =>
                    setRecForm((f) => ({ ...f, payer_company: e.target.value }))
                  }
                >
                  <option value="">— wybierz —</option>
                  {companies.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.short_code}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className={labelCls}>Spółka otrzymująca *</label>
                <select
                  className={inputCls}
                  value={recForm.recipient_company}
                  onChange={(e) =>
                    setRecForm((f) => ({
                      ...f,
                      recipient_company: e.target.value,
                    }))
                  }
                >
                  <option value="">— wybierz —</option>
                  {companies.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.short_code}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-span-2 sm:col-span-1">
                <label className={labelCls}>Opis *</label>
                <input
                  className={inputCls}
                  value={recForm.description}
                  onChange={(e) =>
                    setRecForm((f) => ({ ...f, description: e.target.value }))
                  }
                  placeholder="np. Refaktura czynsz biuro"
                />
              </div>
              <div>
                <label className={labelCls}>Kwota miesięczna (PLN) *</label>
                <input
                  type="number"
                  className={inputCls}
                  value={recForm.amount_monthly}
                  onChange={(e) =>
                    setRecForm((f) => ({
                      ...f,
                      amount_monthly: e.target.value,
                    }))
                  }
                  placeholder="0.00"
                />
              </div>
              <div>
                <label className={labelCls}>Dzień miesiąca *</label>
                <input
                  type="number"
                  min={1}
                  max={31}
                  className={inputCls}
                  value={recForm.day_of_month}
                  onChange={(e) =>
                    setRecForm((f) => ({ ...f, day_of_month: e.target.value }))
                  }
                />
              </div>
              <div>
                <label className={labelCls}>Data od *</label>
                <DatePicker
                  value={recForm.start_date}
                  onChange={(v) =>
                    setRecForm((f) => ({ ...f, start_date: v }))
                  }
                />
              </div>
              <div>
                <label className={labelCls}>Data do (opcjonalnie)</label>
                <DatePicker
                  value={recForm.end_date}
                  onChange={(v) =>
                    setRecForm((f) => ({ ...f, end_date: v }))
                  }
                />
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={saveRec}
                disabled={
                  creatingRec ||
                  !recForm.payer_company ||
                  !recForm.recipient_company ||
                  !recForm.description ||
                  !recForm.amount_monthly
                }
                className="px-4 py-2 bg-[var(--color-primary)] text-white rounded-lg text-sm hover:bg-[var(--color-primary-hover)] disabled:opacity-50"
              >
                {editingRec ? "Zapisz zmiany" : "Dodaj refakturę"}
              </button>
              <button
                onClick={() => {
                  setShowRecForm(false);
                  setEditingRec(null);
                  setRecForm(emptyRecurringForm);
                }}
                className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Anuluj
              </button>
            </div>
          </div>
        )}

        {recurring.length === 0 ? (
          <p className="px-5 py-6 text-sm text-gray-400 text-center">
            Brak refaktur stałych
          </p>
        ) : (
          <>
            {/* Mobile: karty */}
            <div className="sm:hidden divide-y divide-gray-100 dark:divide-gray-700">
              {recurring.map((r) => {
                const isSuspended =
                  !!r.suspended_from &&
                  r.suspended_from <= new Date().toISOString().slice(0, 10);
                return (
                  <div key={r.id} className="px-5 py-4 flex flex-col gap-2">
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-semibold text-sm text-gray-800 dark:text-gray-200">
                        {r.payer_company_code} →{" "}
                        <span className="text-green-700 dark:text-green-400">
                          {r.recipient_company_code}
                        </span>
                      </span>
                      <span className="font-mono text-sm font-semibold text-gray-800 dark:text-gray-200 shrink-0">
                        {fmt(r.amount_monthly)}
                        <span className="text-xs text-gray-400 font-normal">
                          /mies.
                        </span>
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-300">
                      {r.description}
                    </p>
                    <div className="flex items-center gap-3 text-xs text-gray-400">
                      <span>Dzień {r.day_of_month}</span>
                      <span>od {fmtDate(r.start_date)}</span>
                      {r.end_date && <span>do {fmtDate(r.end_date)}</span>}
                      {isSuspended ? (
                        <span className="px-1.5 py-0.5 rounded-full font-medium bg-amber-100 text-amber-700">
                          Wstrzymana
                        </span>
                      ) : (
                        <span className="px-1.5 py-0.5 rounded-full font-medium bg-green-100 text-green-700">
                          Aktywna
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-3 text-xs">
                      <button
                        onClick={() => toggleSuspend(r)}
                        className={`font-medium ${isSuspended ? "text-green-600" : "text-amber-600"}`}
                      >
                        {isSuspended ? "Wznów" : "Wstrzymaj"}
                      </button>
                      <button
                        onClick={() => startEditRec(r)}
                        className="text-[var(--color-primary)] font-medium"
                      >
                        Edytuj
                      </button>
                      <button
                        onClick={() => deleteRec(r.id)}
                        className="text-red-400 hover:text-red-600 ml-auto"
                      >
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Desktop: tabela */}
            <div className="hidden sm:block overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-xs text-gray-400 uppercase tracking-wide border-b border-gray-100 dark:border-gray-700">
                    <th className="px-4 py-2 text-left font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">
                      Płaci
                    </th>
                    <th className="px-4 py-2 text-left font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">
                      Otrzymuje
                    </th>
                    <th className="px-4 py-2 text-left font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">
                      Opis
                    </th>
                    <th className="px-4 py-2 text-right font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">
                      Kwota/mies.
                    </th>
                    <th className="px-4 py-2 text-center font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">
                      Dzień
                    </th>
                    <th className="px-4 py-2 text-left font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">
                      Od
                    </th>
                    <th className="px-4 py-2 text-left font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">
                      Do
                    </th>
                    <th className="px-4 py-2 text-center font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">
                      Status
                    </th>
                    <th className="px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400" />
                  </tr>
                </thead>
                <tbody>
                  {recurring.map((r) => {
                    const isSuspended =
                      !!r.suspended_from &&
                      r.suspended_from <= new Date().toISOString().slice(0, 10);
                    return (
                      <tr
                        key={r.id}
                        className="border-b border-gray-50 dark:border-gray-700/50 hover:bg-gray-50 dark:hover:bg-gray-700/30"
                      >
                        <td className="px-4 py-2 font-medium text-gray-800 dark:text-gray-200">
                          {r.payer_company_code}
                        </td>
                        <td className="px-4 py-2 font-medium text-green-700 dark:text-green-400">
                          {r.recipient_company_code}
                        </td>
                        <td className="px-4 py-2 text-gray-700 dark:text-gray-300">
                          {r.description}
                        </td>
                        <td className="px-4 py-2 text-right font-mono">
                          {fmt(r.amount_monthly)}
                        </td>
                        <td className="px-4 py-2 text-center text-gray-500">
                          {r.day_of_month}
                        </td>
                        <td className="px-4 py-2 text-gray-500">
                          {fmtDate(r.start_date)}
                        </td>
                        <td className="px-4 py-2 text-gray-400">
                          {r.end_date ? fmtDate(r.end_date) : "—"}
                        </td>
                        <td className="px-4 py-2 text-center">
                          {isSuspended ? (
                            <span className="text-xs px-1.5 py-0.5 rounded-full font-medium bg-amber-100 text-amber-700 whitespace-nowrap">
                              Wstrzymana od {fmtDate(r.suspended_from)}
                            </span>
                          ) : (
                            <span className="text-xs px-1.5 py-0.5 rounded-full font-medium bg-green-100 text-green-700">
                              Aktywna
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-2">
                          <div className="flex items-center gap-2 justify-end">
                            <button
                              onClick={() => toggleSuspend(r)}
                              className={`text-xs font-medium ${isSuspended ? "text-green-600 hover:text-green-800" : "text-amber-600 hover:text-amber-800"}`}
                              title={
                                isSuspended
                                  ? "Wznów refakturę"
                                  : "Wstrzymaj od dziś"
                              }
                            >
                              {isSuspended ? "Wznów" : "Wstrzymaj"}
                            </button>
                            <button
                              onClick={() => startEditRec(r)}
                              className="text-xs text-[var(--color-primary)] hover:text-[var(--color-primary)] font-medium"
                            >
                              Edytuj
                            </button>
                            <button
                              onClick={() => deleteRec(r.id)}
                              className="text-red-400 hover:text-red-600"
                            >
                              <Trash2 size={13} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {/* ── Refaktury jednorazowe ──────────────────────────────────────── */}
      <div className="bg-white dark:bg-gray-800 ">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-gray-700">
          <div>
            <h3 className="font-semibold text-gray-900 dark:text-gray-100">
              Refaktury jednorazowe
            </h3>
            <p className="text-xs text-gray-400 mt-0.5">
              Jednorazowe przeniesienia kosztów między spółkami GK
            </p>
          </div>
          <button
            onClick={() => {
              setOtForm(emptyOneTimeForm);
              setShowOtForm((v) => !v);
            }}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-[var(--color-primary)] text-white rounded-lg text-sm hover:bg-[var(--color-primary-hover)]"
          >
            <Plus size={14} /> Dodaj
          </button>
        </div>

        {showOtForm && (
          <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/30">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-3">
              <div>
                <label className={labelCls}>Spółka płacąca *</label>
                <select
                  className={inputCls}
                  value={otForm.payer_company}
                  onChange={(e) =>
                    setOtForm((f) => ({ ...f, payer_company: e.target.value }))
                  }
                >
                  <option value="">— wybierz —</option>
                  {companies.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.short_code}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className={labelCls}>Spółka otrzymująca *</label>
                <select
                  className={inputCls}
                  value={otForm.recipient_company}
                  onChange={(e) =>
                    setOtForm((f) => ({
                      ...f,
                      recipient_company: e.target.value,
                    }))
                  }
                >
                  <option value="">— wybierz —</option>
                  {companies.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.short_code}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className={labelCls}>Opis *</label>
                <input
                  className={inputCls}
                  value={otForm.description}
                  onChange={(e) =>
                    setOtForm((f) => ({ ...f, description: e.target.value }))
                  }
                  placeholder="np. Refaktura usługi doradczej"
                />
              </div>
              <div>
                <label className={labelCls}>Kwota (PLN) *</label>
                <input
                  type="number"
                  className={inputCls}
                  value={otForm.amount}
                  onChange={(e) =>
                    setOtForm((f) => ({ ...f, amount: e.target.value }))
                  }
                  placeholder="0.00"
                />
              </div>
              <div>
                <label className={labelCls}>Data *</label>
                <DatePicker
                  value={otForm.cost_date}
                  onChange={(v) =>
                    setOtForm((f) => ({ ...f, cost_date: v }))
                  }
                />
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={saveOt}
                disabled={
                  creatingOt ||
                  !otForm.payer_company ||
                  !otForm.recipient_company ||
                  !otForm.description ||
                  !otForm.amount
                }
                className="px-4 py-2 bg-[var(--color-primary)] text-white rounded-lg text-sm hover:bg-[var(--color-primary-hover)] disabled:opacity-50"
              >
                Dodaj refakturę
              </button>
              <button
                onClick={() => setShowOtForm(false)}
                className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Anuluj
              </button>
            </div>
          </div>
        )}

        {oneTime.length === 0 ? (
          <p className="px-5 py-6 text-sm text-gray-400 text-center">
            Brak refaktur jednorazowych
          </p>
        ) : (
          <div className="bg-white dark:bg-gray-800 overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-xs text-gray-400 uppercase tracking-wide border-b border-gray-100 dark:border-gray-700">
                  <th className="px-4 py-2 text-left font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">
                    Płaci
                  </th>
                  <th className="px-4 py-2 text-left font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">
                    Otrzymuje
                  </th>
                  <th className="px-4 py-2 text-left font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">
                    Opis
                  </th>
                  <th className="px-4 py-2 text-right font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">
                    Kwota
                  </th>
                  <th className="px-4 py-2 text-left font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">
                    Data
                  </th>
                  <th className="px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400" />
                </tr>
              </thead>
              <tbody>
                {oneTime.map((r) => (
                  <tr
                    key={r.id}
                    className="border-b border-gray-50 dark:border-gray-700/50 hover:bg-gray-50 dark:hover:bg-gray-700/30"
                  >
                    <td className="px-4 py-2 font-medium text-gray-800 dark:text-gray-200">
                      {r.payer_company_code}
                    </td>
                    <td className="px-4 py-2 font-medium text-green-700 dark:text-green-400">
                      {r.recipient_company_code}
                    </td>
                    <td className="px-4 py-2 text-gray-700 dark:text-gray-300">
                      {r.description}
                    </td>
                    <td className="px-4 py-2 text-right font-mono">
                      {fmt(r.amount)}
                    </td>
                    <td className="px-4 py-2 text-gray-500">
                      {fmtDate(r.cost_date)}
                    </td>
                    <td className="px-4 py-2 text-right">
                      <button
                        onClick={() => deleteOt(r.id)}
                        className="text-red-400 hover:text-red-600"
                      >
                        <Trash2 size={13} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────

const TABS: { id: Tab; label: string; shortLabel: string }[] = [
  { id: "wykres", label: "Wykres cashflow", shortLabel: "Wykres" },
  { id: "salda", label: "Salda kont i majątek", shortLabel: "Salda" },
  { id: "vat", label: "VAT / CIT", shortLabel: "VAT/CIT" },
  { id: "refaktury", label: "Refaktury wewnętrzne", shortLabel: "Refaktury" },
];

export function GroupFinancePage() {
  const { user } = useAuthStore();
  const isCeo = user?.is_ceo ?? false;
  const isAccountant = user?.role === "accountant";
  const hasFullAccess = isCeo || isAccountant;

  const [searchParams, setSearchParams] = useSearchParams();
  const rawTab = searchParams.get("tab") as Tab | null;

  // Directors (non-CEO, non-accountant) only see the cashflow chart tab
  const visibleTabs = hasFullAccess
    ? TABS
    : TABS.filter((t) => t.id === "wykres");
  const validTab =
    rawTab && visibleTabs.some((t) => t.id === rawTab) ? rawTab : null;
  const tab: Tab = validTab ?? "wykres";

  const handleTabChange = (id: Tab) => {
    setSearchParams(id !== "wykres" ? { tab: id } : {}, { replace: true });
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-gray-100">
            Finanse GK ELEKTIM
          </h1>
          <div className="w-8 h-0.5 bg-[var(--color-primary)] mt-1 mb-1" />
          <p className="text-xs text-gray-400 mt-0.5">
            Panel widoczny tylko dla dyrekcji i księgowości
          </p>
        </div>
      </div>

      {/* Tab bar — hidden for directors with only one tab */}
      {visibleTabs.length > 1 && (
        <div className="flex border-b border-gray-200 dark:border-gray-700">
          {visibleTabs.map((t) => (
            <button
              key={t.id}
              onClick={() => handleTabChange(t.id)}
              className={`flex-1 sm:flex-none px-2 sm:px-4 py-2 text-xs sm:text-sm font-medium border-b-2 transition-colors text-center ${
                tab === t.id
                  ? "border-[var(--color-primary)] text-[var(--color-primary)]"
                  : "border-transparent text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
              }`}
            >
              <span className="sm:hidden">{t.shortLabel}</span>
              <span className="hidden sm:inline">{t.label}</span>
            </button>
          ))}
        </div>
      )}

      {tab === "wykres" && <WykresTab />}
      {hasFullAccess && tab === "salda" && <BalancesTab />}
      {hasFullAccess && tab === "vat" && <VATTab />}
      {hasFullAccess && tab === "refaktury" && <RefakturyTab />}
    </div>
  );
}
